<template>
    <transition name="quiz-modal-fade">
        <div
            v-if="open"
            class="quiz-modal-overlay"
            @mousedown.self="close"
            tabindex="-1"
            ref="overlay"
        >
            <div
                class="quiz-modal"
                :class="{
                    'quiz-modal--split': isSplitLayout,
                    'quiz-modal--days': currentStep === 'days',
                    'quiz-modal--result': currentStep === 'result',
                    'quiz-modal--form': currentStep === 'email'
                }"
                role="dialog"
                aria-modal="true"
                :aria-label="introTitle"
            >
                <div class="quiz-modal__frame">
                    <!-- Header -->
                    <header class="quiz-modal__header">
                        <button
                            v-if="canGoBack && currentStep !== 'days' && currentStep !== 'result'"
                            type="button"
                            class="quiz-modal__back"
                            aria-label="Back"
                            @click="goBack"
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
                                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            class="quiz-modal__close"
                            aria-label="Close"
                            @click="close"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="1.45" stroke-linecap="round" />
                            </svg>
                        </button>
                        <div
                            v-if="currentStep && !['redirect', 'error', 'loading'].includes(currentStep)"
                            class="quiz-modal__bar"
                            :class="{ 'quiz-modal__bar--back': canGoBack && currentStep !== 'days' && currentStep !== 'result' }"
                        >
                            <div class="quiz-modal__progress">
                                <div class="quiz-modal__progress-fill" :style="{ transform: 'scaleX(' + ((currentStep === 'result' ? 100 : displayPct) / 100) + ')' }" />
                            </div>
                            <div class="quiz-modal__bar-info">
                                <template v-if="currentStep === 'result' && result">
                                    <span class="quiz-modal__bar-match">{{ resultBarLabel }}</span>
                                </template>
                                <template v-else>
                                    <span class="quiz-modal__bar-pct">
                                        {{ t('progress.profile', { pct: displayPct }) }}
                                    </span>
                                    <span class="quiz-modal__bar-disc">{{ progressHint }}</span>
                                </template>
                            </div>
                        </div>
                    </header>

                    <!-- Body -->
                    <div class="quiz-modal__body" ref="body">
                        <!-- INTRO -->
                        <section v-if="!currentStep" class="quiz-screen quiz-screen--intro">
                            <div class="quiz-intro">
                                <div class="quiz-intro__media">
                                    <img src="/img/quiz/carnival-hero.jpg" alt="" class="quiz-intro__hero" />
                                </div>
                                <div class="quiz-intro__content">
                                    <h1 class="quiz-screen__title quiz-screen__title--intro">
                                        <template v-if="hasSplitIntroTitle">
                                            <span class="quiz-screen__title-lead">{{ introTitleLead }}</span>
                                            <span class="quiz-screen__title-line">
                                                <span class="quiz-screen__title-accent">{{ introTitleAccent }}</span>
                                                <span
                                                    v-if="introTitleTail"
                                                    class="quiz-screen__title-tail"
                                                > {{ introTitleTail }}</span>
                                            </span>
                                        </template>
                                        <template v-else>
                                            {{ introTitle }}
                                        </template>
                                    </h1>
                                    <p class="quiz-screen__lead">{{ t('intro.lead') }}</p>
                                    <div class="quiz-reward">
                                        <div class="quiz-reward__icon" aria-hidden="true">
                                            <img
                                                src="/img/quiz/koko-point.png"
                                                alt=""
                                                class="quiz-reward__koko"
                                            />
                                        </div>
                                        <div class="quiz-reward__divider" aria-hidden="true"></div>
                                        <div class="quiz-reward__copy">
                                            <span class="quiz-reward__eyebrow">{{ rewardEyebrow }}</span>
                                            <strong class="quiz-reward__value">{{ t('intro.reward_percent') }}</strong>
                                            <p>{{ t('intro.reward_body') }}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        class="quiz-btn quiz-btn--primary quiz-btn--with-arrow"
                                        @click="startQuiz"
                                    >
                                        <span>{{ t('intro.start') }}</span>
                                        <span class="quiz-btn__arrow" aria-hidden="true">
                                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                                <path d="M8 12h8m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </span>
                                    </button>
                                    <button
                                        v-if="hasExistingProgress"
                                        type="button"
                                        class="quiz-btn quiz-btn--ghost"
                                        @click="restoreProgress"
                                    >
                                        {{ resumeLabel }}
                                    </button>
                                    <p class="quiz-note">{{ t('intro.note') }}</p>
                                </div>
                            </div>
                        </section>

                    <!-- ORIGIN -->
                    <section v-else-if="currentStep === 'origin'" class="quiz-screen">
                        <!-- Confirm card (medium confidence) -->
                        <template v-if="showOriginConfirm">
                            <div class="quiz-q-head">
                                <span class="quiz-q-count">{{ t('questions.origin.confirm_title') }}</span>
                                <em class="quiz-q-hint">{{ t('questions.origin.confirm_hint') }}</em>
                            </div>
                            <div class="quiz-confirm-card">
                                <p class="quiz-confirm-q">{{ t('questions.origin.confirm_question', { from: originFromLabel }) }}</p>
                                <p class="quiz-confirm-why">
                                    {{ originDetection.geo === 'BR'
                                        ? t('questions.origin.confirm_why_br')
                                        : t('questions.origin.confirm_why_mismatch') }}
                                </p>
                                <div class="quiz-confirm-row">
                                    <button
                                        type="button"
                                        class="quiz-btn quiz-btn--primary"
                                        @click="confirmOriginYes"
                                    >
                                        {{ t('questions.origin.confirm_yes', { from: originFromLabel }) }}
                                    </button>
                                    <button
                                        type="button"
                                        class="quiz-btn quiz-btn--ghost"
                                        @click="showOriginGrid = true"
                                    >
                                        {{ t('questions.origin.confirm_no') }}
                                    </button>
                                </div>
                            </div>
                        </template>
                        <!-- Full grid -->
                        <template v-else>
                            <div class="quiz-q-head">
                                <span class="quiz-q-count">{{ questionLabel }}</span>
                                <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                            </div>
                            <h2 class="quiz-screen__title">{{ t('questions.origin.title') }}</h2>
                            <p class="quiz-screen__help">{{ t('questions.origin.help') }}</p>
                            <div class="quiz-origin-grid">
                                <button
                                    v-for="o in originList"
                                    :key="o.id"
                                    type="button"
                                    class="quiz-origin-cell"
                                    :class="{ picked: answers.origin === o.id }"
                                    @click="selectOrigin(o.id)"
                                >
                                    {{ o.label }}
                                </button>
                                <button
                                    type="button"
                                    class="quiz-origin-cell"
                                    @click="selectBrazil"
                                >
                                    {{ t('questions.origin.brazil') }}
                                </button>
                            </div>
                        </template>
                    </section>

                    <!-- REDIRECT (Brazil) -->
                    <section v-else-if="currentStep === 'redirect'" class="quiz-screen quiz-screen--redirect">
                        <h2 class="quiz-screen__title">{{ t('redirect.title') }}</h2>
                        <p class="quiz-screen__help">{{ t('redirect.body') }}</p>
                        <a
                            class="quiz-btn quiz-btn--primary"
                            href="https://www.camarotecarnaval.com/"
                            target="_blank"
                            rel="noopener"
                        >{{ t('redirect.cta') }}</a>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--link"
                            @click="showOriginGrid = true; currentStep = 'origin'"
                        >
                            {{ t('redirect.not_brazil') }}
                        </button>
                    </section>

                    <!-- DAYS -->
                    <section v-else-if="currentStep === 'days'" class="quiz-screen quiz-screen--days">
                        <div class="quiz-split">
                            <div class="quiz-intro__content quiz-days">
                                <h2 class="quiz-screen__title quiz-days__title">
                                    <template v-if="daysTitleLead && daysTitleAccent">
                                        <span class="quiz-screen__title-lead">{{ daysTitleLead }} </span><span class="quiz-screen__title-accent">{{ daysTitleAccent }}</span>
                                    </template>
                                    <template v-else>{{ t('questions.days.title') }}</template>
                                </h2>
                                <p class="quiz-screen__help">{{ t('questions.days.help') }}</p>
                                <div class="quiz-days__board">
                                    <div
                                        v-for="(cards, groupKey, index) in dayGroups"
                                        v-if="cards.length"
                                        :key="groupKey"
                                        class="quiz-day-group"
                                    >
                                        <div class="quiz-day-group__header" :style="{ gridColumn: index + 1 }">
                                            <span
                                                class="quiz-day-group__icon"
                                                :class="'quiz-day-group__icon--' + groupKey"
                                                aria-hidden="true"
                                            >
                                                <svg v-if="groupKey === 'ouro'" viewBox="0 0 32 32" fill="none">
                                                    <path d="M6.5 23c3.4-1.7 6.4-2.5 9.5-2.5s6.1.8 9.5 2.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                                                    <path d="M7.6 21.6 10 11.2 16 16.6 22 11.2 24.4 21.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                                                    <circle cx="10" cy="9.4" r="1.45" fill="currentColor" />
                                                    <circle cx="16" cy="6.8" r="1.45" fill="currentColor" />
                                                    <circle cx="22" cy="9.4" r="1.45" fill="currentColor" />
                                                </svg>
                                                <svg v-else-if="groupKey === 'campeas'" viewBox="0 0 32 32" fill="none">
                                                    <path d="M11 7.5h10v7.2a5 5 0 0 1-10 0V7.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                                                    <path d="M11 9.2H8.2A3.2 3.2 0 0 0 11 14.2M21 9.2h2.8A3.2 3.2 0 0 1 21 14.2M16 19.7V23M12.2 24.5h7.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                                <svg v-else viewBox="0 0 32 32" fill="none">
                                                    <path d="M16 6.2 18.7 12l6.4.7-4.8 4.3 1.4 6.3L16 20.2 10.3 23.3l1.4-6.3L6.9 12.7 13.3 12 16 6.2Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
                                                </svg>
                                            </span>
                                            <div class="quiz-day-group__copy">
                                                <strong>{{ groupName(groupKey) }}</strong>
                                                <span>{{ groupBlurb(groupKey) }}</span>
                                            </div>
                                        </div>
                                        <div class="quiz-day-grid" :style="{ gridColumn: index + 1 }">
                                            <button
                                                v-for="card in cards"
                                                :key="card.date"
                                                type="button"
                                                class="quiz-day-card"
                                                :class="{
                                                    soldout: card.soldout,
                                                    selected: selectedDays.includes(card.date)
                                                }"
                                                :aria-pressed="String(selectedDays.includes(card.date))"
                                                :disabled="card.soldout"
                                                @click="toggleDay(card.date)"
                                            >
                                                <small class="quiz-day-card__month">{{ monthAbbr(card.date) }}</small>
                                                <b class="quiz-day-card__day">{{ card.n }}</b>
                                                <small class="quiz-day-card__weekday">{{ weekdayAbbr(card.date) }}</small>
                                                <span class="quiz-day-card__box" aria-hidden="true"></span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="quiz-days__foot">
                                    <button
                                        type="button"
                                        class="quiz-btn quiz-btn--link"
                                        @click="skipDays"
                                    >
                                        {{ t('questions.days.not_sure') }}
                                    </button>
                                    <div class="quiz-days__actions">
                                        <button
                                            type="button"
                                            class="quiz-btn quiz-btn--ghost"
                                            @click="goBack"
                                        >
                                            {{ t('questions.common.back') }}
                                        </button>
                                        <button
                                            type="button"
                                            class="quiz-btn quiz-btn--primary quiz-btn--with-arrow"
                                            :disabled="!selectedDays.length"
                                            @click="confirmDays"
                                        >
                                            <span>{{ t('questions.common.continue') }}</span>
                                            <span class="quiz-btn__arrow" aria-hidden="true">
                                                <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                                    <path d="M8 12h8m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <!-- BUDGET -->
                    <section v-else-if="currentStep === 'budget'" class="quiz-screen">
                        <div class="quiz-q-head">
                            <span class="quiz-q-count">{{ questionLabel }}</span>
                            <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                        </div>
                        <h2 class="quiz-screen__title">{{ t('questions.budget.title') }}</h2>
                        <p class="quiz-screen__help">{{ t('questions.budget.help') }}</p>
                        <div class="quiz-slider">
                            <div class="quiz-slider__value">
                                <strong>{{ formatPrice(budgetMin) }} – {{ formatPrice(budgetMax) }}</strong>
                                <span>{{ t('questions.common.per_person') }}</span>
                            </div>
                            <div
                                ref="budgetRange"
                                class="quiz-range"
                                @pointerdown="onRangePointerDown"
                            >
                                <div class="quiz-range__track">
                                    <div class="quiz-range__fill" :style="budgetFillStyle()"></div>
                                </div>
                                <input
                                    type="range"
                                    :min="budgetFloor"
                                    :max="budgetCeil"
                                    :step="budgetStep"
                                    :value="budgetMin"
                                    class="quiz-range__input quiz-range__input--min"
                                    :class="{ 'is-active': budgetThumb === 'min' }"
                                    aria-label="Minimum"
                                    @input="setBudgetMin"
                                />
                                <input
                                    type="range"
                                    :min="budgetFloor"
                                    :max="budgetCeil"
                                    :step="budgetStep"
                                    :value="budgetMax"
                                    class="quiz-range__input quiz-range__input--max"
                                    :class="{ 'is-active': budgetThumb === 'max' }"
                                    aria-label="Maximum"
                                    @input="setBudgetMax"
                                />
                                <span
                                    class="quiz-range__thumb quiz-range__thumb--min"
                                    :class="{ 'is-active': budgetThumb === 'min' }"
                                    :style="{ left: 'calc(12px + (100% - 24px) * ' + budgetPct(budgetMin) / 100 + ')' }"
                                    aria-hidden="true"
                                ></span>
                                <span
                                    class="quiz-range__thumb quiz-range__thumb--max"
                                    :class="{ 'is-active': budgetThumb === 'max' }"
                                    :style="{ left: 'calc(12px + (100% - 24px) * ' + budgetPct(budgetMax) / 100 + ')' }"
                                    aria-hidden="true"
                                ></span>
                            </div>
                            <div class="quiz-slider__scale">
                                <span>{{ formatPrice(budgetFloor) }}</span>
                                <span>{{ formatPrice(budgetCeil) }}</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--primary"
                            @click="confirmBudget"
                        >
                            {{ t('questions.common.confirm') }}
                        </button>
                        <div v-if="trendLabel" class="quiz-trend">
                            {{ trendLabel }}
                        </div>
                    </section>

                    <!-- CREW + PARTY SIZE -->
                    <section v-else-if="currentStep === 'crew'" class="quiz-screen">
                        <div class="quiz-q-head">
                            <span class="quiz-q-count">{{ questionLabel }}</span>
                            <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                        </div>
                        <h2 class="quiz-screen__title">{{ t('questions.crew.title') }}</h2>
                        <p class="quiz-screen__help">{{ t('questions.crew.help') }}</p>
                        <div class="quiz-opts quiz-opts--art">
                            <button
                                v-for="opt in crewOptions"
                                :key="opt.id"
                                type="button"
                                class="quiz-opt"
                                :class="{ picked: crewPick === opt.id }"
                                @click="selectCrew(opt.id)"
                            >
                                <img
                                    v-if="crewArt(opt.id)"
                                    :src="crewArt(opt.id)"
                                    alt=""
                                    class="quiz-opt__art"
                                />
                                <span class="quiz-opt__copy">
                                    <span class="quiz-opt__label">{{ opt.label }}</span>
                                    <span v-if="opt.sub" class="quiz-opt__sub">{{ opt.sub }}</span>
                                </span>
                            </button>
                        </div>
                        <div v-if="crewPick" class="quiz-count">
                            <span class="quiz-count__label">{{ t('questions.crew.count') }}</span>
                            <button
                                type="button"
                                class="quiz-count__btn"
                                :disabled="partySize <= partyMin"
                                @click="bumpParty(-1)"
                            >−</button>
                            <strong class="quiz-count__value">{{ partySize }}</strong>
                            <button
                                type="button"
                                class="quiz-count__btn"
                                :disabled="partySize >= partyMax"
                                @click="bumpParty(1)"
                            >+</button>
                        </div>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--primary"
                            :disabled="!crewPick"
                            @click="confirmCrew"
                        >
                            {{ t('questions.common.continue') }}
                        </button>
                    </section>

                    <!-- CHOICE (seat, view, festa, camarote, box) -->
                    <section v-else-if="isChoiceStep" class="quiz-screen">
                        <div class="quiz-q-head">
                            <span class="quiz-q-count">{{ questionLabel }}</span>
                            <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                        </div>
                        <h2 class="quiz-screen__title">{{ choiceTitle }}</h2>
                        <p class="quiz-screen__help">{{ choiceHelp }}</p>
                        <div class="quiz-opts" :class="{ 'quiz-opts--art': usesOptionArt }">
                            <button
                                v-for="opt in choiceOptions"
                                :key="opt.id"
                                type="button"
                                class="quiz-opt"
                                :class="{ picked: answers[currentStep] === opt.id }"
                                @click="chooseOption(currentStep, opt.id)"
                            >
                                <img
                                    v-if="optionArt(opt.id)"
                                    :src="optionArt(opt.id)"
                                    alt=""
                                    class="quiz-opt__art"
                                />
                                <span v-else class="quiz-opt__mark" :class="'quiz-opt__mark--' + opt.id" v-html="optionGlyph(opt.id)"></span>
                                <span class="quiz-opt__copy">
                                    <span class="quiz-opt__label">{{ opt.label }}</span>
                                    <span v-if="opt.sub" class="quiz-opt__sub">{{ opt.sub }}</span>
                                </span>
                            </button>
                        </div>
                        <div v-if="trendLabel" class="quiz-trend">
                            {{ trendLabel }}
                        </div>
                    </section>

                    <!-- POSITION -->
                    <section v-else-if="currentStep === 'position'" class="quiz-screen">
                        <div class="quiz-q-head">
                            <span class="quiz-q-count">{{ questionLabel }}</span>
                            <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                        </div>
                        <h2 class="quiz-screen__title">{{ t('questions.position.title') }}</h2>
                        <p class="quiz-screen__help">{{ t('questions.position.help') }}</p>
                        <div class="quiz-opts">
                            <button
                                v-for="opt in positionChoices"
                                :key="opt.id"
                                type="button"
                                class="quiz-opt"
                                @click="choosePosition(opt)"
                            >
                                <span class="quiz-opt__mark" :class="'quiz-opt__mark--' + opt.id" v-html="optionGlyph(opt.id)"></span>
                                <span class="quiz-opt__copy">
                                    <span class="quiz-opt__label">{{ positionLabel(opt.id) }}</span>
                                    <span class="quiz-opt__sub">{{ positionSub(opt.id) }}</span>
                                </span>
                            </button>
                            <button
                                type="button"
                                class="quiz-opt"
                                @click="choosePosition(null)"
                            >
                                <span class="quiz-opt__mark quiz-opt__mark--tanto" v-html="optionGlyph('tanto')"></span>
                                <span class="quiz-opt__copy">
                                    <span class="quiz-opt__label">{{ t('questions.common.no_preference') }}</span>
                                    <span class="quiz-opt__sub">{{ t('questions.common.no_preference_sub') }}</span>
                                </span>
                            </button>
                        </div>
                    </section>

                    <!-- NAME -->
                    <section v-else-if="currentStep === 'name'" class="quiz-screen">
                        <div class="quiz-q-head">
                            <span class="quiz-q-count">{{ questionLabel }}</span>
                            <em class="quiz-q-hint">{{ t('progress.question_hint') }}</em>
                        </div>
                        <h2 class="quiz-screen__title">{{ t('questions.name.title') }}</h2>
                        <p class="quiz-screen__help">{{ t('questions.name.help') }}</p>
                        <div class="quiz-namebox">
                            <input
                                ref="nameInput"
                                v-model="fullName"
                                type="text"
                                autocomplete="given-name"
                                maxlength="30"
                                :placeholder="t('questions.name.placeholder')"
                                class="quiz-input"
                                @keydown.enter="submitName"
                            />
                            <p v-if="cleanedName" class="quiz-name-hi">
                                {{ t('questions.name.greeting', { name: cleanedName }) }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--primary"
                            @click="submitName"
                        >
                            {{ t('questions.name.continue') }}
                        </button>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--link"
                            @click="skipName"
                        >
                            {{ t('questions.name.skip') }}
                        </button>
                    </section>

                    <!-- EMAIL (unlock) -->
                    <section v-else-if="currentStep === 'email'" class="quiz-screen">
                        <template v-if="!emailUnlocked">
                            <h2 class="quiz-screen__title">{{ emailTitle }}</h2>
                            <p class="quiz-screen__help">{{ emailHelp }}</p>
                            <div class="quiz-email-form">
                                <label class="quiz-field">
                                    <span class="quiz-field__label">{{ nameFieldLabel }}</span>
                                    <input
                                        ref="nameInput"
                                        v-model="fullName"
                                        type="text"
                                        autocomplete="given-name"
                                        maxlength="30"
                                        :placeholder="t('questions.name.placeholder')"
                                        class="quiz-input"
                                    />
                                </label>
                                <label class="quiz-field">
                                    <span class="quiz-field__label">{{ emailFieldLabel }}</span>
                                    <input
                                        ref="emailInput"
                                        v-model="email"
                                        type="email"
                                        autocomplete="email"
                                        inputmode="email"
                                        :placeholder="t('questions.email.placeholder')"
                                        class="quiz-input"
                                        @keydown.enter="submitEmail"
                                    />
                                </label>
                                <p v-if="emailError" class="quiz-error" role="alert">{{ emailError }}</p>
                                <label class="quiz-optin">
                                    <input type="checkbox" v-model="marketingOptIn" />
                                    {{ t('opt_in') }}
                                </label>
                                <button
                                    type="button"
                                    class="quiz-btn quiz-btn--primary quiz-btn--with-arrow"
                                    @click="submitEmail"
                                >
                                    <span>{{ t('questions.email.cta') }}</span>
                                    <span class="quiz-btn__arrow" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                            <path d="M8 12h8m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                                <p class="quiz-fine-print">{{ t('questions.email.fine_print') }}</p>
                            </div>
                        </template>
                        <template v-else>
                            <h2 class="quiz-screen__title">
                                {{ t('questions.email.revealed_title', { pct: promoTier }) }}
                            </h2>
                            <p class="quiz-screen__help">
                                {{ t('questions.email.sent', { email: email.trim() }) }}
                            </p>
                            <div v-if="promoCode" class="quiz-promo quiz-promo--reveal">
                                <div class="quiz-promo__tier">
                                    <strong>{{ promoTier }}%</strong>
                                    <small>{{ t('discount.unlocked_small') }}</small>
                                </div>
                                <div class="quiz-promo__code">
                                    <b>{{ promoCode }}</b>
                                    <button type="button" @click="copyCode">
                                        {{ copiedCode ? t('questions.email.copied') : t('questions.email.copy') }}
                                    </button>
                                </div>
                            </div>
                            <p v-else class="quiz-screen__help">
                                {{ t('questions.email.revealed_pending') }}
                            </p>
                            <button
                                type="button"
                                class="quiz-btn quiz-btn--primary"
                                @click="continueAfterEmail"
                            >
                                {{ t('questions.email.continue') }}
                            </button>
                        </template>
                    </section>

                    <!-- LOADING -->
                    <section v-else-if="currentStep === 'loading'" class="quiz-screen quiz-screen--loading">
                        <h2 class="quiz-screen__title">{{ loadingTitle }}</h2>
                        <div class="quiz-loading-steps">
                            <div
                                class="quiz-loading-step"
                                :class="{ on: loadingStep >= 1 }"
                            >
                                <span class="quiz-loading-step__icon">✓</span>
                                {{ t('loading.step_1') }}
                            </div>
                            <div
                                class="quiz-loading-step"
                                :class="{ on: loadingStep >= 2 }"
                            >
                                <span class="quiz-loading-step__icon">✓</span>
                                {{ t('loading.step_2') }}
                            </div>
                            <div
                                class="quiz-loading-step"
                                :class="{ on: loadingStep >= 3 }"
                            >
                                <span class="quiz-loading-step__icon">✓</span>
                                {{ t('loading.step_3') }}
                            </div>
                        </div>
                    </section>

                    <!-- ERROR (empty inventory / no recommendation) -->
                    <section v-else-if="currentStep === 'error'" class="quiz-screen quiz-screen--error">
                        <p class="quiz-screen__help" role="alert">{{ t('errors.inventory') }}</p>
                        <button
                            type="button"
                            class="quiz-btn quiz-btn--primary"
                            @click="close"
                        >
                            {{ t('errors.close') }}
                        </button>
                    </section>

                    <!-- RESULT -->
                    <section v-else-if="currentStep === 'result' && result" class="quiz-screen quiz-screen--result">
                        <div class="quiz-match">
                            <div class="quiz-match__copy">
                                <span class="quiz-match__eyebrow">{{ bestMatchLabel }}</span>
                                <h2 class="quiz-match__name">{{ resultProductName }}</h2>
                                <p v-if="personalLead" class="quiz-match__hello">{{ personalLead }}</p>
                                <p class="quiz-match__meta">
                                    <span v-if="resultGroupName">{{ resultGroupName }}</span>
                                    <span v-if="resultDateShort">{{ resultDateShort }}</span>
                                    <span v-if="result.sku.sec">{{ sectorChip }}</span>
                                    <span v-if="resultRowLabel">{{ resultRowLabel }}</span>
                                </p>
                                <p v-if="result.sku.over" class="quiz-match__over">{{ overBudgetLabel }}</p>
                                <p v-if="resultProductDesc" class="quiz-match__desc">{{ resultProductDesc }}</p>
                                <div class="quiz-match__facts">
                                    <div class="quiz-match__price">
                                        <strong>{{ formatPrice(result.sku.p) }}</strong>
                                        <span>{{ t('questions.common.per_person') }}</span>
                                        <span v-if="groupTotalLabel" class="quiz-match__total">{{ groupTotalLabel }}</span>
                                    </div>
                                    <div v-if="emailUnlocked && promoCode" class="quiz-match__off">
                                        <span class="quiz-match__promo-label">{{ promoLabel }}</span>
                                        <strong class="quiz-match__code">{{ promoCode }}</strong>
                                        <span>{{ offLabel }}</span>
                                    </div>
                                </div>
                                <button
                                    v-if="offer && !emailUnlocked"
                                    type="button"
                                    class="quiz-btn quiz-btn--primary quiz-btn--with-arrow"
                                    @click="claimDiscount"
                                >
                                    <span>{{ claimLabel }}</span>
                                    <span class="quiz-btn__arrow" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                            <path d="M8 12h8m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                                <button
                                    v-else-if="offerStatus !== 'pending'"
                                    type="button"
                                    class="quiz-btn quiz-btn--primary quiz-btn--with-arrow"
                                    @click="navigateToResult"
                                >
                                    <span>{{ resultBuyLabel }}</span>
                                    <span class="quiz-btn__arrow" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                                            <path d="M8 12h8m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                                <button
                                    v-if="offer && !emailUnlocked"
                                    type="button"
                                    class="quiz-btn quiz-btn--link"
                                    @click="navigateToResult"
                                >
                                    {{ buyWithoutLabel }}
                                </button>
                                <p v-if="emailUnlocked && promoCode" class="quiz-match__applied">{{ appliedLabel }}</p>
                                <p v-else-if="offerStatus === 'pending'" class="quiz-match__applied">{{ checkingDiscountLabel }}</p>
                                <p v-else-if="offerStatus === 'none'" class="quiz-match__applied">{{ noDiscountLabel }}</p>
                            </div>
                            <img :src="resultArt" alt="" class="quiz-match__art" />
                        </div>

                        <div v-if="resultReasons.length" class="quiz-why">
                            <h3>
                                <span class="quiz-why__spark" aria-hidden="true"></span>
                                {{ whyTitle }}
                            </h3>
                            <ul>
                                <li v-for="(reason, i) in resultReasons" :key="i">
                                    <span class="quiz-why__check" aria-hidden="true">✓</span>
                                    <div>
                                        <strong>{{ reason.title }}</strong>
                                        <span v-if="reason.sub">{{ reason.sub }}</span>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div class="quiz-result__footer">
                            <button
                                v-if="canRefine"
                                type="button"
                                class="quiz-btn quiz-btn--link"
                                @click="refineMore"
                            >
                                {{ t('result.refine_cta') }}
                            </button>
                            <button
                                type="button"
                                class="quiz-btn quiz-btn--link"
                                @click="restart"
                            >
                                {{ t('result.redo') }}
                            </button>
                        </div>
                    </section>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import {
    pushDataLayer,
    createEventId,
    trackGenerateLead
} from "../../../utils/gtm";
import engineModule from "../../../quiz/engine";
import inventoryModule from "../../../quiz/inventoryAdapter";
import { submitQuizLead } from "../../../quiz/olsForms";
import { getPromoCode, promoCoversTicket, validatePromo } from "../../../quiz/promocodes";
import bookAnswersModule from "../../../quiz/bookAnswers";

const { quizBookAnswers } = bookAnswersModule;

const { createEngine, constants } = engineModule;
const { ticketsToInventory } = inventoryModule;

const LS_KEY = "bkq_progress";
const CHOICE_STEPS = ["seat", "view", "festa", "camarote", "box"];

const C2O = {
    AR: "latam", CL: "latam", PE: "latam", CO: "latam", UY: "latam",
    MX: "mx", US: "us", CA: "ca", GB: "uk", IE: "uk",
    FR: "fr", DE: "dach", CH: "dach", AT: "dach",
    ES: "eu", IT: "eu", PT: "eu", NL: "eu", SE: "eu", NO: "eu",
    AU: "oc", NZ: "oc", JP: "as", CN: "as", KR: "as", IN: "as",
    AE: "as", SA: "as", TR: "as"
};
const TZ_MAP = {
    "America/Sao_Paulo": "BR", "America/Fortaleza": "BR", "America/Recife": "BR",
    "Europe/Paris": "FR", "Europe/Berlin": "DE", "Europe/Zurich": "CH",
    "Europe/Vienna": "AT", "Europe/London": "GB", "Europe/Dublin": "IE",
    "Europe/Madrid": "ES", "Europe/Rome": "IT", "Europe/Lisbon": "PT",
    "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US",
    "America/Los_Angeles": "US", "America/Toronto": "CA", "America/Vancouver": "CA",
    "America/Argentina/Buenos_Aires": "AR", "America/Buenos_Aires": "AR",
    "America/Santiago": "CL", "America/Lima": "PE", "America/Bogota": "CO",
    "America/Mexico_City": "MX", "Australia/Sydney": "AU", "Pacific/Auckland": "NZ",
    "Asia/Tokyo": "JP", "Asia/Dubai": "AE", "Asia/Singapore": "SG"
};

function langToCountry(l) {
    if (!l) return null;
    const parts = l.split("-");
    const region = (parts[1] || "").toUpperCase();
    if (region && C2O[region]) return region;
    return { pt: "BR", fr: "FR", de: "DE", it: "IT", ja: "JP", en: "US", nl: "NL", es: "AR", tr: "TR" }[parts[0]] || null;
}

function cleanName(s) {
    const f = (s || "").trim().split(/\s+/)[0] || "";
    return f ? f.charAt(0).toUpperCase() + f.slice(1).toLowerCase() : "";
}

function writtenName(s) {
    return (s || "")
        .trim()
        .replace(/\s+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

export default {
    name: "TicketQuizModal",
    props: {
        open: { type: Boolean, default: false },
        olsBase: { type: String, default: "" },
        lang: { type: String, default: "en" },
        trans: { type: Object, default: () => ({}) },
        tickets: { type: Array, default: () => [] },
        promoCodes: { type: Object, default: () => ({}) },
        productSlugs: { type: Object, default: () => ({}) },
        page: { type: Object, default: () => ({}) },
        currencySymbol: { type: String, default: "US$" },
        promoSite: { type: String, default: "" },
        /** When shop /api/promocode is down, still surface the configured code (local/dev). */
        promoSoftFail: { type: Boolean, default: false }
    },
    data() {
        return {
            currentStep: null,
            answers: {},
            asked: [],
            meta: {},
            originDone: false,
            refine: false,
            showOriginGrid: false,
            selectedDays: [],
            budgetMin: 30,
            budgetMax: 1500,
            budgetFloor: 30,
            budgetCeil: 1500,
            budgetThumb: "max",
            crewPick: "",
            partySize: 2,
            partyMin: 1,
            partyMax: 12,
            offer: null,
            offerStatus: "idle",
            offerToken: 0,
            leadSnapshot: {},
            leadSent: false,
            fullName: "",
            email: "",
            emailError: "",
            marketingOptIn: false,
            nameSkipped: false,
            emailUnlocked: false,
            promoCode: null,
            promoTier: 0,
            shownPct: 0,
            loadingStep: 0,
            loadingTimers: [],
            result: null,
            engine: null,
            inventory: [],
            originDetection: null,
            copiedCode: false,
            hasExistingProgress: false
        };
    },
    computed: {
        slugToEngineKey() {
            const inv = {};
            if (!this.productSlugs) return inv;
            Object.keys(this.productSlugs).forEach(engineKey => {
                const slug = this.productSlugs[engineKey];
                // arq + arq9 share "grandstands"; keep arq — init() upgrades sector 9 → arq9
                if (inv[slug] && engineKey === "arq9") return;
                inv[slug] = engineKey;
            });
            return inv;
        },
        budgetStep() {
            const span = this.budgetCeil - this.budgetFloor;
            if (span <= 100) return 1;
            if (span <= 1000) return 5;
            return 10;
        },
        profilePct() {
            const required = ["days", "festa", "crew", "budget"];
            const done = required.filter(q => this.asked.includes(q)).length;
            const extra = ["seat", "view", "box", "camarote"].filter(q => this.asked.includes(q)).length;
            if (this.currentStep === "result") return 100;
            return Math.min(96, Math.round((done / required.length) * 80 + extra * 4));
        },
        displayPct() {
            return Math.max(this.shownPct, this.profilePct);
        },
        currentTier() {
            return (this.offer && this.offer.pct) || this.promoTier || 0;
        },
        crewOptions() {
            const q = this.trans && this.trans.questions && this.trans.questions.crew;
            if (!q || !q.options) return [];
            return Object.keys(q.options).map(id => ({
                id,
                label: q.options[id].label || "",
                sub: q.options[id].sub || ""
            }));
        },
        isChoiceStep() {
            return CHOICE_STEPS.includes(this.currentStep);
        },
        isSplitLayout() {
            return !this.currentStep || this.currentStep === "days";
        },
        canGoBack() {
            if (!this.currentStep) return false;
            if (["loading", "result", "redirect", "error"].includes(this.currentStep)) return false;
            return this.asked.length > 0 || this.originDone;
        },
        questionNumber() {
            return this.asked.filter(q => q !== "origin").length + 1;
        },
        questionLabel() {
            return this.t("progress.question", { n: this.questionNumber });
        },
        originList() {
            const origins = (this.trans && this.trans.origins) || {};
            return Object.keys(origins).map(id => ({
                id,
                label: origins[id].label || id,
                from: origins[id].from || ""
            }));
        },
        showOriginConfirm() {
            return (
                this.originDetection &&
                this.originDetection.conf === "medium" &&
                this.originDetection.origin &&
                !this.showOriginGrid
            );
        },
        originFromLabel() {
            if (!this.originDetection || !this.originDetection.origin) return "";
            const o = (this.trans && this.trans.origins && this.trans.origins[this.originDetection.origin]) || {};
            return o.from || "";
        },
        dayCards() {
            const daysConst = constants.DEFAULT_DAYS;
            return Object.keys(daysConst).map(date => {
                const info = daysConst[date];
                const rows = this.inventory.filter(r => r.day === date && r.av > 0);
                const minPrice = rows.length ? Math.min(...rows.map(r => r.p)) : null;
                const soldout = rows.length === 0;
                const totalAv = rows.reduce((s, r) => s + r.av, 0);
                const lowStock = totalAv > 0 && totalAv < 50;
                return { date, ...info, minPrice, soldout, lowStock };
            });
        },
        dayGroups() {
            const groups = { ouro: [], especial: [], campeas: [] };
            this.dayCards.forEach(card => {
                if (groups[card.g]) groups[card.g].push(card);
            });
            return groups;
        },
        daysButtonLabel() {
            if (!this.selectedDays.length) return this.t("questions.common.continue");
            const tpl = this.t("questions.common.continue_with_nights", { count: this.selectedDays.length });
            if (typeof tpl === "string" && tpl.includes("|")) {
                const parts = tpl.split("|");
                return this.selectedDays.length === 1 ? parts[0] : parts[1];
            }
            return tpl;
        },
        choiceTitle() {
            const q = this.trans && this.trans.questions && this.trans.questions[this.currentStep];
            return (q && q.title) || "";
        },
        choiceHelp() {
            const q = this.trans && this.trans.questions && this.trans.questions[this.currentStep];
            return (q && q.help) || "";
        },
        usesOptionArt() {
            return this.currentStep === "crew" || this.currentStep === "view" || this.currentStep === "seat" || this.currentStep === "festa" || this.currentStep === "box" || this.currentStep === "camarote";
        },
        choiceOptions() {
            if (!this.isChoiceStep) return [];
            const q = this.trans && this.trans.questions && this.trans.questions[this.currentStep];
            if (!q || !q.options) return [];
            return Object.keys(q.options).map(id => ({
                id,
                label: q.options[id].label || "",
                sub: q.options[id].sub || ""
            }));
        },
        positionChoices() {
            if (!this.engine) return [];
            return this.engine.positionOptions(this.answers);
        },
        trendLabel() {
            if (!this.engine || !this.answers.origin) return "";
            const st = this.engine.computeState(this.answers);
            if (!st.top) return "";
            const name = this.productName(st.top);
            return this.t("progress.trend", { product: name });
        },
        cleanedName() {
            return cleanName(this.fullName);
        },
        resumeLabel() {
            if (this.answers.name) {
                return this.t("intro.resume_with_name", { name: cleanName(this.answers.name) });
            }
            return this.t("intro.resume");
        },
        emailTitle() {
            const label = this.t("questions.email.gate_title");
            if (label !== "questions.email.gate_title") return label;
            return this.t("questions.email.title", { pct: this.currentTier });
        },
        emailHelp() {
            const label = this.t("questions.email.help");
            return label === "questions.email.help"
                ? "Unlock the discount and see your ideal ticket."
                : label;
        },
        nameFieldLabel() {
            const label = this.t("questions.email.name_label");
            return label === "questions.email.name_label" ? this.t("questions.name.placeholder") : label;
        },
        emailFieldLabel() {
            const label = this.t("questions.email.email_label");
            return label === "questions.email.email_label" ? "Email" : label;
        },
        daysTitleLead() {
            const title = this.t("questions.days.title_lead");
            return title === "questions.days.title_lead" ? "" : title;
        },
        daysTitleAccent() {
            const title = this.t("questions.days.title_accent");
            return title === "questions.days.title_accent" ? "" : title;
        },
        introTitleLead() {
            const title = this.t("intro.title_lead");
            return title === "intro.title_lead" ? "" : title;
        },
        introTitleAccent() {
            const title = this.t("intro.title_accent");
            return title === "intro.title_accent" ? "" : title;
        },
        introTitleTail() {
            const title = this.t("intro.title_tail");
            return title === "intro.title_tail" ? "" : title;
        },
        hasSplitIntroTitle() {
            return !!(this.introTitleLead && this.introTitleAccent);
        },
        introTitle() {
            if (this.hasSplitIntroTitle) {
                const tail = this.introTitleTail ? ` ${this.introTitleTail}` : "";
                return `${this.introTitleLead} ${this.introTitleAccent}${tail}`;
            }
            return this.t("intro.title");
        },
        rewardEyebrow() {
            const title = this.t("intro.reward_eyebrow");
            return title === "intro.reward_eyebrow" ? "" : title;
        },
        loadingTitle() {
            if (this.answers.name) {
                return this.t("loading.title_with_name", { name: cleanName(this.answers.name) });
            }
            return this.t("loading.title");
        },
        resultHello() {
            if (this.answers.name) {
                return this.t("result.hello", { name: cleanName(this.answers.name) });
            }
            return this.t("result.hello_generic");
        },
        resultGroupName() {
            if (!this.result || !this.result.sku) return "";
            const day = this.result.sku.day;
            const tickets = this.tickets || [];
            const hit = tickets.find(t => {
                const d = String(t.small_date || t.date || "").slice(0, 10);
                return d === day && t.group;
            });
            if (hit && typeof hit.group === "string" && !/^\d+$/.test(hit.group)) {
                return hit.group;
            }
            const meta = constants.DEFAULT_DAYS[day];
            return meta ? this.groupName(meta.g) : "";
        },
        resultRowLabel() {
            const sku = this.result && this.result.sku;
            if (!sku || sku.row == null || String(sku.row).trim() === "") return "";
            const label = this.t("result.row", { n: sku.row });
            return label === "result.row" ? "Row " + sku.row : label;
        },
        resultProductName() {
            return this.result ? this.productName(this.result.k) : "";
        },
        resultProductDesc() {
            return this.result ? this.productDesc(this.result.k) : "";
        },
        resultDayLabel() {
            if (!this.result) return "";
            const day = constants.DEFAULT_DAYS[this.result.sku.day];
            if (!day) return this.result.sku.day;
            return `${day.w} ${day.n}`;
        },
        resultDateShort() {
            if (!this.result || !this.result.sku || !this.result.sku.day) return "";
            const day = constants.DEFAULT_DAYS[this.result.sku.day];
            return this.monthAbbr(this.result.sku.day) + " " + (day ? day.n : "");
        },
        sectorChip() {
            if (!this.result || !this.result.sku || !this.result.sku.sec) return "";
            const label = this.t("result.sector_short", { n: this.result.sku.sec });
            return label === "result.sector_short" ? "Sector " + this.result.sku.sec : label;
        },
        sectorLabel() {
            if (!this.result || !this.result.sku || !this.result.sku.sec) return "";
            const label = this.t("result.sector_fit", { n: this.result.sku.sec });
            return label === "result.sector_fit" ? "Sector " + this.result.sku.sec : label;
        },
        resultFirstName() {
            return cleanName(this.answers.name);
        },
        bestMatchLabel() {
            if (this.resultFirstName) {
                const named = this.t("result.best_match_named", { name: this.resultFirstName });
                if (named !== "result.best_match_named") return named;
                return this.resultFirstName + "'s match";
            }
            const label = this.t("result.best_match");
            return label === "result.best_match" ? "Your best match" : label;
        },
        personalLead() {
            if (!this.resultFirstName) return "";
            const lead = this.t("result.personal_lead", { name: this.resultFirstName });
            if (lead !== "result.personal_lead") return lead;
            return this.resultFirstName + ", this is the night we shaped around what you told us.";
        },
        whyTitle() {
            if (this.resultFirstName) {
                const named = this.t("result.why_title_named", { name: this.resultFirstName });
                if (named !== "result.why_title_named") return named;
                return "Why this fits you, " + this.resultFirstName;
            }
            const label = this.t("result.why_title");
            return label === "result.why_title" ? "Why we picked this for you" : label;
        },
        resultBuyLabel() {
            const label = this.t("result.buy_package");
            if (label === "result.buy_package") {
                return this.t("result.buy_cta", { product: this.resultProductName });
            }
            return label;
        },
        resultArt() {
            const crew = this.crewArt(this.answers && this.answers.crew);
            return crew || "/img/quiz/result-seats.jpg";
        },
        progressHint() {
            const label = this.t("discount.finish_for");
            return label === "discount.finish_for" ? "Your package comes after these answers" : label;
        },
        resultBarLabel() {
            const label = this.t("result.ready");
            return label === "result.ready" ? "Your package" : label;
        },
        overBudgetLabel() {
            const label = this.t("result.over_budget");
            return label === "result.over_budget" ? "This is the closest ticket. It sits outside the range you set." : label;
        },
        groupTotalLabel() {
            const n = Number(this.answers.partySize) || 1;
            if (!this.result || n <= 1) return "";
            const label = this.t("result.group_total", {
                count: n,
                price: this.formatPrice(this.result.sku.p * n)
            });
            return label === "result.group_total" ? "Total for " + n + " people: " + this.formatPrice(this.result.sku.p * n) : label;
        },
        buyWithoutLabel() {
            const label = this.t("result.buy_without");
            return label === "result.buy_without" ? "Buy without the discount" : label;
        },
        checkingDiscountLabel() {
            const label = this.t("result.checking_discount");
            return label === "result.checking_discount" ? "Checking whether this ticket has a discount." : label;
        },
        noDiscountLabel() {
            const label = this.t("result.no_discount");
            return label === "result.no_discount" ? "This ticket has no quiz discount." : label;
        },
        claimLabel() {
            const label = this.t("result.claim", { pct: this.currentTier });
            return label === "result.claim" ? "Claim " + this.currentTier + "% off" : label;
        },
        promoLabel() {
            const label = this.t("result.promo_label");
            return label === "result.promo_label" ? "Promocode" : label;
        },
        offLabel() {
            const label = this.t("result.off_unlocked", { pct: this.currentTier });
            return label === "result.off_unlocked" ? this.currentTier + "% OFF unlocked" : label;
        },
        appliedLabel() {
            const label = this.t("result.applied", { pct: this.currentTier });
            return label === "result.applied" ? this.currentTier + "% discount automatically applied" : label;
        },
        resultNightNote() {
            if (!this.result) return "";
            const day = constants.DEFAULT_DAYS[this.result.sku.day];
            if (!day) return "";
            const g = (this.trans && this.trans.groups && this.trans.groups[day.g]) || {};
            return g.note || "";
        },
        resultReasons() {
            if (!this.result || !this.engine) return [];
            return this.buildReasons(this.result.k, this.answers, this.result.sku);
        },
        canRefine() {
            return false;
        },
        refineBody() {
            if (!this.engine) return "";
            const left = this.engine.remainingCore(this.asked);
            const tpl = this.t("result.refine_body", { count: left });
            if (typeof tpl === "string" && tpl.includes("|")) {
                const parts = tpl.split("|");
                return left === 1 ? parts[0] : parts[1];
            }
            return tpl;
        }
    },
    watch: {
        open: {
            immediate: true,
            handler(v) {
                if (v) {
                    this.bindKeydown();
                    this.init();
                } else {
                    this.unbindKeydown();
                }
            }
        },
        profilePct(v) {
            if (v > this.shownPct) this.shownPct = v;
        }
    },
    methods: {
        t(key, replacements) {
            const parts = key.split(".");
            let val = this.trans;
            for (const p of parts) {
                if (!val || typeof val !== "object") return key;
                val = val[p];
            }
            if (val == null) return key;
            if (typeof val !== "string") return String(val);
            if (replacements) {
                Object.keys(replacements).forEach(k => {
                    val = val.split(`:${k}`).join(String(replacements[k]));
                });
            }
            return val;
        },
        formatPrice(v) {
            if (v == null || !Number.isFinite(Number(v))) return "";
            return this.currencySymbol + Number(v).toLocaleString("en-US");
        },
        productName(k) {
            const p = this.trans && this.trans.products && this.trans.products[k];
            return (p && p.name) || k;
        },
        productDesc(k) {
            const p = this.trans && this.trans.products && this.trans.products[k];
            return (p && p.desc) || "";
        },
        optionArt(id) {
            const sets = {
                crew: {
                    solo: "crew-solo.jpg",
                    casal: "crew-couple.jpg",
                    amigos: "crew-friends.jpg",
                    criancas: "crew-family.jpg",
                    idosos: "crew-travelers.jpg"
                },
                view: {
                    perto: "view-runway-night.jpg",
                    alto: "view-above-night.jpg",
                    tanto: "view-whatever-v2.jpg"
                },
                seat: {
                    marcado: "seat-guaranteed.jpg",
                    livre: "seat-standing-up.jpg",
                    tanto: "seat-unsure.jpg"
                },
                festa: {
                    inclusa: "festa-party-pista.jpg",
                    desfile: "festa-parade.jpg",
                    tanto: "festa-either.jpg"
                },
                box: {
                    sim: "box-share.jpg",
                    nao: "box-private.jpg"
                },
                camarote: {
                    folia: "camarote-folia.jpg",
                    mar: "camarote-mar.jpg",
                    tanto: "camarote-either.jpg"
                }
            };
            const files = sets[this.currentStep];
            if (!files || !files[id]) return "";
            return "/img/quiz/" + files[id];
        },
        optionGlyph(id) {
            const stroke = 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"';
            const person = '<circle cx="16" cy="11" r="3" ' + stroke + '/><path d="M10.2 23.2c.7-3 2.8-4.6 5.8-4.6s5.1 1.6 5.8 4.6" ' + stroke + '/>';
            const glyphs = {
                solo: '<svg viewBox="0 0 32 32">' + person + '</svg>',
                casal: '<svg viewBox="0 0 32 32"><circle cx="12" cy="12" r="2.6" ' + stroke + '/><circle cx="20.5" cy="12" r="2.6" ' + stroke + '/><path d="M7.2 23c.6-2.6 2.2-4 4.8-4s4.2 1.4 4.8 4M16.2 23c.5-2.2 1.8-3.4 4-3.4 2.3 0 3.6 1.2 4.2 3.4" ' + stroke + '/></svg>',
                amigos: '<svg viewBox="0 0 32 32"><circle cx="10" cy="12.5" r="2.3" ' + stroke + '/><circle cx="16" cy="10.5" r="2.5" ' + stroke + '/><circle cx="22" cy="12.5" r="2.3" ' + stroke + '/><path d="M6.2 23c.5-2.2 1.8-3.4 3.8-3.4 1.6 0 2.8.8 3.4 2M12.4 23c.5-2.4 2-3.8 3.6-3.8s3.1 1.4 3.6 3.8M18.6 21.6c.6-1.2 1.8-2 3.4-2 2 0 3.3 1.2 3.8 3.4" ' + stroke + '/></svg>',
                criancas: '<svg viewBox="0 0 32 32"><circle cx="16" cy="13" r="3.2" ' + stroke + '/><path d="M11 23.5c.6-2.6 2.4-4 5-4s4.4 1.4 5 4" ' + stroke + '/><path d="M16 6.2v2.2M12.6 7.6l1.4 1.6M19.4 7.6 18 9.2" ' + stroke + '/></svg>',
                idosos: '<svg viewBox="0 0 32 32"><path d="M8 20.5h16v3.2a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-3.2Z" ' + stroke + '/><path d="M10 20.5V15a6 6 0 0 1 12 0v5.5" ' + stroke + '/></svg>',
                marcado: '<svg viewBox="0 0 32 32"><path d="M9 14.5h14v8.2a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2v-8.2Z" ' + stroke + '/><path d="M12 14.5V12a4 4 0 0 1 8 0v2.5M13.5 20.2l1.7 1.7 3.4-3.6" ' + stroke + '/></svg>',
                livre: '<svg viewBox="0 0 32 32"><circle cx="16" cy="9.5" r="2.4" ' + stroke + '/><path d="M16 12.2v5.2M16 17.4l-3.2 5.2M16 17.4l3.2 5.2M12.8 14.8h6.4" ' + stroke + '/></svg>',
                perto: '<svg viewBox="0 0 32 32"><path d="M6 22h20M8 22V12.5L16 8l8 4.5V22" ' + stroke + '/><path d="M13 22v-5h6v5" ' + stroke + '/></svg>',
                alto: '<svg viewBox="0 0 32 32"><path d="M6 23h20M8 23l5-12h6l5 12" ' + stroke + '/><path d="M13 16.5h6" ' + stroke + '/></svg>',
                inclusa: '<svg viewBox="0 0 32 32"><path d="M11 8.5h4.2l1 6.2H10l1-6.2Z" ' + stroke + '/><path d="M10 14.7h8.2L17 23.5H11.2L10 14.7Z" ' + stroke + '/><path d="M19.2 12.2c1.6.4 2.6 1.6 2.8 3.2M21 10.2c2.2.6 3.6 2.2 3.8 4.4" ' + stroke + '/></svg>',
                desfile: '<svg viewBox="0 0 32 32"><path d="M5 21h22M8 21l2.2-7h11.6L24 21" ' + stroke + '/><circle cx="11" cy="23.2" r="1.6" ' + stroke + '/><circle cx="21" cy="23.2" r="1.6" ' + stroke + '/><path d="M14 14v-3.2h4V14" ' + stroke + '/></svg>',
                folia: '<svg viewBox="0 0 32 32"><path d="M12 22.5c4.5-1 7-4.2 8.2-9.2" ' + stroke + '/><path d="M18.6 8.8c.2 2.2-1.2 3.6-3.4 4.2 1.6 1.2 1.6 3.2.2 4.8" ' + stroke + '/><circle cx="22.5" cy="20.5" r="1.3" fill="currentColor"/></svg>',
                mar: '<svg viewBox="0 0 32 32"><path d="M6 14c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2M6 20c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" ' + stroke + '/></svg>',
                start: '<svg viewBox="0 0 32 32"><path d="M10 23V9M10 10h10l-2.2 3.4L20 16.8H10" ' + stroke + '/></svg>',
                drum1: '<svg viewBox="0 0 32 32"><path d="M9 13h14v7.2a4 4 0 0 1-4 4h-6a4 4 0 0 1-4-4V13Z" ' + stroke + '/><path d="M9 16.2h14M12.2 9.2l2.2 3.8M19.8 9.2 17.6 13" ' + stroke + '/></svg>',
                center: '<svg viewBox="0 0 32 32"><circle cx="16" cy="16" r="7.2" ' + stroke + '/><circle cx="16" cy="16" r="2" fill="currentColor"/><path d="M16 5.5v2.4M16 24.1v2.4M5.5 16h2.4M24.1 16h2.4" ' + stroke + '/></svg>',
                drum2: '<svg viewBox="0 0 32 32"><path d="M9 13h14v7.2a4 4 0 0 1-4 4h-6a4 4 0 0 1-4-4V13Z" ' + stroke + '/><path d="M9 16.2h14M12.2 9.2l2.2 3.8M19.8 9.2 17.6 13" ' + stroke + '/></svg>',
                local: '<svg viewBox="0 0 32 32"><path d="M16 24.5s-6.2-4.6-6.2-9.2a6.2 6.2 0 0 1 12.4 0c0 4.6-6.2 9.2-6.2 9.2Z" ' + stroke + '/><circle cx="16" cy="15" r="2" ' + stroke + '/></svg>',
                end: '<svg viewBox="0 0 32 32"><path d="M8 24V8M8 9h12l-2 3.2L20 15.4H8" ' + stroke + '/><path d="M20 22.5l2.2 2.2 4.2-4.4" ' + stroke + '/></svg>',
                tanto: '<svg viewBox="0 0 32 32"><circle cx="10" cy="16" r="1.4" fill="currentColor"/><circle cx="16" cy="16" r="1.4" fill="currentColor"/><circle cx="22" cy="16" r="1.4" fill="currentColor"/></svg>',
                sim: '<svg viewBox="0 0 32 32"><path d="M8 16.5 13.2 22 24 10" ' + stroke + '/></svg>',
                nao: '<svg viewBox="0 0 32 32"><path d="M10 10l12 12M22 10 10 22" ' + stroke + '/></svg>'
            };
            return glyphs[id] || glyphs.tanto;
        },
        groupName(g) {
            const gr = this.trans && this.trans.groups && this.trans.groups[g];
            return (gr && gr.name) || g;
        },
        dateLocale() {
            return {
                en: "en-US",
                pt: "pt-BR",
                es: "es",
                fr: "fr",
                de: "de",
                it: "it",
                tr: "tr"
            }[this.lang] || "en-US";
        },
        monthAbbr(date) {
            const d = new Date(String(date) + "T12:00:00");
            if (isNaN(d.getTime())) return "";
            return d.toLocaleDateString(this.dateLocale(), { month: "short" }).replace(".", "").slice(0, 3);
        },
        weekdayAbbr(date) {
            const d = new Date(String(date) + "T12:00:00");
            if (isNaN(d.getTime())) return "";
            return d.toLocaleDateString(this.dateLocale(), { weekday: "short" }).replace(".", "").slice(0, 3);
        },
        dayPad(n) {
            const v = String(n);
            return v.length < 2 ? "0" + v : v;
        },
        groupBlurb(g) {
            const gr = this.trans && this.trans.groups && this.trans.groups[g];
            return (gr && gr.blurb) || "";
        },
        positionLabel(id) {
            const opts = this.trans && this.trans.questions && this.trans.questions.position && this.trans.questions.position.options;
            return (opts && opts[id] && opts[id].label) || id;
        },
        positionSub(id) {
            const opts = this.trans && this.trans.questions && this.trans.questions.position && this.trans.questions.position.options;
            return (opts && opts[id] && opts[id].sub) || "";
        },
        bindKeydown() {
            if (typeof document === "undefined" || this._keydownBound) return;
            document.addEventListener("keydown", this.onKeydown);
            this._keydownBound = true;
        },
        unbindKeydown() {
            if (typeof document === "undefined" || !this._keydownBound) return;
            document.removeEventListener("keydown", this.onKeydown);
            this._keydownBound = false;
        },
        onKeydown(e) {
            if (e.key === "Escape" || e.key === "Esc") this.close();
        },
        init() {
            const inv = ticketsToInventory(this.tickets, this.slugToEngineKey) || [];
            inv.forEach(r => {
                if (r.k === "arq" && r.sec === "9") r.k = "arq9";
            });
            this.inventory = inv;
            if (!inv.length) {
                this.engine = null;
                this.currentStep = "error";
                return;
            }
            if (this.currentStep === "error") this.currentStep = null;
            this.engine = createEngine({ inv });
            this.originDetection = this.detectOrigin();
            this.checkSavedProgress();
            this.$nextTick(() => {
                if (this.$refs.overlay) this.$refs.overlay.focus();
            });
        },
        detectOrigin() {
            let tz = "";
            try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) { /* noop */ }
            const geo = window.BK_GEO || TZ_MAP[tz] || null;
            const lang = navigator.language || "";
            const lc = langToCountry(lang);
            const lb = (lang.split("-")[0] || "").toLowerCase();
            const out = { geo, lang, conf: "low", origin: null, redirect: false, brazil: false };
            if (!geo) return out;
            if (geo === "BR") {
                // Brazilians stay in the quiz: skip Camarote redirect and origin, go to next Q.
                out.brazil = true;
                out.conf = "high";
                out.redirect = false;
                out.origin = null;
                return out;
            }
            const o = C2O[geo] || "ot";
            if (!lc || C2O[lc] === o || (lb === "en" && ["us", "uk", "ca", "oc"].includes(o))) {
                out.conf = "high"; out.origin = o;
            } else {
                out.conf = "medium"; out.origin = o;
            }
            return out;
        },
        checkSavedProgress() {
            try {
                const raw = window.localStorage.getItem(LS_KEY);
                if (!raw) return;
                const prog = JSON.parse(raw);
                this.hasExistingProgress = !!(
                    prog && prog.ans && prog.hist && prog.hist.length > 0
                );
            } catch (e) {
                this.hasExistingProgress = false;
            }
        },
        persist() {
            try {
                window.localStorage.setItem(LS_KEY, JSON.stringify({
                    ans: this.answers,
                    meta: this.meta,
                    hist: this.asked,
                    cur: this.currentStep,
                    originDone: this.originDone
                }));
            } catch (e) { /* storage unavailable */ }
        },
        flowNext() {
            if (!this.engine) return "days";
            const nxt = this.engine.nextStep(this.answers, this.asked);
            if (nxt === "result" && !this.asked.includes("email")) return "email";
            return nxt;
        },
        advance() {
            const nxt = this.flowNext();
            if (nxt === "result") {
                this.showLoading();
                return;
            }
            if (nxt === "budget" && this.answers.budget === undefined) this.syncBudgetBounds();
            if (nxt === "crew" && this.answers.crew) this.crewPick = this.answers.crew;
            this.currentStep = nxt;
            this.persist();
            this.scrollTop();
            this.$nextTick(() => {
                if (nxt === "name" && this.$refs.nameInput) {
                    this.$refs.nameInput.focus({ preventScroll: true });
                }
                if (nxt === "email" && this.$refs.emailInput) {
                    this.$refs.emailInput.focus({ preventScroll: true });
                }
            });
        },
        answer(id, val) {
            this.$set(this.answers, id, val);
            if (!this.asked.includes(id)) this.asked.push(id);
            this.persist();
            setTimeout(() => this.advance(), 300);
        },
        goBack() {
            if (this.currentStep === "email" && this.result) {
                this.currentStep = "result";
                this.scrollTop();
                return;
            }
            if (this.currentStep === "origin" && !this.asked.length) {
                this.currentStep = null;
                this.originDone = false;
                return;
            }
            if (!this.asked.length) return;
            const id = this.asked.pop();
            this.$delete(this.answers, id);
            if (id === "budget") {
                this.$delete(this.answers, "budgetMin");
                this.syncBudgetBounds();
            }
            if (id === "crew") this.crewPick = "";
            if (id === "name") { this.nameSkipped = false; delete this.meta.nameSkipped; }
            if (id === "position") this.$delete(this.answers, "positionSecs");
            if (id === "origin") this.originDone = false;
            if (id === "email") {
                this.emailUnlocked = false;
                this.promoCode = null;
            }
            this.currentStep = id;
            this.persist();
            this.scrollTop();
        },
        startQuiz() {
            this.resetState();
            this.originDetection = this.detectOrigin();
            if (this.originDetection) {
                this.meta.originSrc = this.originDetection.brazil ? "auto_br" : "auto";
                if (this.originDetection.origin) this.meta.originDetected = this.originDetection.origin;
            }
            this.advance();
        },
        skipOriginAsBrazil(src) {
            this.$delete(this.answers, "origin");
            this.meta.originSrc = src || "brazil";
            this.originDone = true;
            if (!this.asked.includes("origin")) this.asked.push("origin");
            this.persist();
            this.advance();
        },
        selectBrazil() {
            this.skipOriginAsBrazil("brazil");
        },
        resetState() {
            this.answers = {};
            this.asked = [];
            this.meta = {};
            this.originDone = false;
            this.refine = false;
            this.showOriginGrid = false;
            this.selectedDays = [];
            this.budgetMin = 30;
            this.budgetMax = 1500;
            this.budgetFloor = 30;
            this.budgetCeil = 1500;
            this.budgetThumb = "max";
            this.crewPick = "";
            this.partySize = 2;
            this.partyMin = 1;
            this.partyMax = 12;
            this.offer = null;
            this.offerStatus = "idle";
            this.offerToken += 1;
            this.leadSnapshot = {};
            this.leadSent = false;
            this.fullName = "";
            this.email = "";
            this.emailError = "";
            this.marketingOptIn = false;
            this.nameSkipped = false;
            this.emailUnlocked = false;
            this.promoCode = null;
            this.promoTier = 0;
            this.shownPct = 0;
            this.loadingStep = 0;
            this.result = null;
            this.copiedCode = false;
            try { window.localStorage.removeItem(LS_KEY); } catch (e) { /* noop */ }
            this.clearStoredPromo();
        },
        restoreProgress() {
            try {
                const raw = window.localStorage.getItem(LS_KEY);
                if (!raw) { this.startQuiz(); return; }
                const prog = JSON.parse(raw);
                this.answers = prog.ans || {};
                this.meta = prog.meta || {};
                this.asked = prog.hist || [];
                this.originDone = !!prog.originDone;
                if (this.answers.name) this.fullName = this.answers.name;
                if (this.answers.days) this.selectedDays = [...this.answers.days];
                if (this.answers.budget) this.budgetMax = this.answers.budget;
                if (this.answers.budgetMin) this.budgetMin = this.answers.budgetMin;
                if (this.answers.crew) this.crewPick = this.answers.crew;
                if (this.answers.partySize) this.partySize = this.answers.partySize;
                this.currentStep = prog.cur || null;
                if (this.currentStep === "result" || this.currentStep === "loading") {
                    this.advance();
                }
            } catch (e) {
                this.startQuiz();
            }
        },
        restart() {
            this.resetState();
            this.currentStep = null;
        },
        selectOrigin(id) {
            this.meta.originSrc = (
                this.originDetection &&
                this.originDetection.origin === id &&
                this.originDetection.conf !== "low"
            ) ? "confirmado" : "perguntado";
            this.originDone = true;
            this.answer("origin", id);
        },
        confirmOriginYes() {
            this.meta.originSrc = "confirmado";
            this.originDone = true;
            this.answer("origin", this.originDetection.origin);
        },
        toggleDay(date) {
            const idx = this.selectedDays.indexOf(date);
            if (idx >= 0) {
                this.selectedDays.splice(idx, 1);
            } else {
                this.selectedDays.push(date);
            }
        },
        confirmDays() {
            if (!this.selectedDays.length) return;
            delete this.meta.daysAny;
            this.answer("days", [...this.selectedDays].sort());
        },
        skipDays() {
            this.meta.daysAny = true;
            this.answer("days", []);
        },
        budgetFillStyle() {
            const floor = this.budgetFloor;
            const ceil = this.budgetCeil;
            const span = Math.max(ceil - floor, 1);
            const left = ((this.budgetMin - floor) / span) * 100;
            const width = ((this.budgetMax - this.budgetMin) / span) * 100;
            return { left: left + "%", width: width + "%" };
        },
        setBudgetMin(event) {
            const next = Number(event.target.value);
            this.budgetThumb = "min";
            this.budgetMin = Math.min(next, this.budgetMax);
            event.target.value = this.budgetMin;
        },
        setBudgetMax(event) {
            const next = Number(event.target.value);
            this.budgetThumb = "max";
            this.budgetMax = Math.max(next, this.budgetMin);
            event.target.value = this.budgetMax;
        },
        budgetPct(value) {
            const span = Math.max(this.budgetCeil - this.budgetFloor, 1);
            return ((value - this.budgetFloor) / span) * 100;
        },
        budgetValueAt(clientX) {
            const el = this.$refs.budgetRange;
            if (!el) return this.budgetMin;
            const rect = el.getBoundingClientRect();
            const inset = 12;
            const usable = Math.max(rect.width - inset * 2, 1);
            const ratio = Math.min(Math.max((clientX - rect.left - inset) / usable, 0), 1);
            const raw = this.budgetFloor + ratio * (this.budgetCeil - this.budgetFloor);
            const step = this.budgetStep;
            return Math.min(Math.max(Math.round(raw / step) * step, this.budgetFloor), this.budgetCeil);
        },
        moveBudgetThumb(clientX) {
            const value = this.budgetValueAt(clientX);
            if (this.budgetThumb === "min") {
                this.budgetMin = Math.min(value, this.budgetMax);
            } else {
                this.budgetMax = Math.max(value, this.budgetMin);
            }
        },
        onRangePointerDown(event) {
            if (event.pointerType === "mouse" && event.button !== 0) return;
            event.preventDefault();
            const value = this.budgetValueAt(event.clientX);
            const toMin = Math.abs(value - this.budgetMin);
            const toMax = Math.abs(value - this.budgetMax);
            if (toMin === toMax) {
                this.budgetThumb = value <= this.budgetMin ? "min" : "max";
            } else {
                this.budgetThumb = toMin < toMax ? "min" : "max";
            }
            this.moveBudgetThumb(event.clientX);
            const target = event.currentTarget;
            const move = (e) => this.moveBudgetThumb(e.clientX);
            const up = () => {
                target.removeEventListener("pointermove", move);
                target.removeEventListener("pointerup", up);
                target.removeEventListener("pointercancel", up);
            };
            if (target.setPointerCapture) target.setPointerCapture(event.pointerId);
            target.addEventListener("pointermove", move);
            target.addEventListener("pointerup", up);
            target.addEventListener("pointercancel", up);
        },
        syncBudgetBounds() {
            if (!this.engine) return;
            const bounds = this.engine.priceBounds(this.answers);
            this.budgetFloor = bounds.min;
            this.budgetCeil = bounds.max;
            this.budgetMin = bounds.min;
            this.budgetMax = bounds.max;
        },
        confirmBudget() {
            this.$set(this.answers, "budgetMin", this.budgetMin);
            this.answer("budget", this.budgetMax);
        },
        crewLimits(id) {
            const spec = {
                solo: [1, 1, 1],
                casal: [2, 8, 2],
                amigos: [3, 12, 4],
                criancas: [2, 12, 3],
                idosos: [2, 8, 2]
            };
            return spec[id] || [1, 12, 2];
        },
        selectCrew(id) {
            const limits = this.crewLimits(id);
            this.crewPick = id;
            this.partyMin = limits[0];
            this.partyMax = limits[1];
            this.partySize = limits[2];
        },
        bumpParty(delta) {
            const next = this.partySize + delta;
            if (next < this.partyMin || next > this.partyMax) return;
            this.partySize = next;
        },
        confirmCrew() {
            if (!this.crewPick) return;
            this.$set(this.answers, "partySize", this.partySize);
            this.answer("crew", this.crewPick);
        },
        crewArt(id) {
            const files = {
                solo: "crew-solo.jpg",
                casal: "crew-couple.jpg",
                amigos: "crew-friends.jpg",
                criancas: "crew-family.jpg",
                idosos: "crew-travelers.jpg"
            };
            return files[id] ? "/img/quiz/" + files[id] : "";
        },
        chooseOption(id, val) {
            this.answer(id, val);
        },
        choosePosition(opt) {
            if (opt) {
                this.$set(this.answers, "positionSecs", opt.hit || []);
                this.answer("position", opt.id);
            } else {
                this.$set(this.answers, "positionSecs", []);
                this.answer("position", "tanto");
            }
        },
        submitName() {
            const n = cleanName(this.fullName);
            if (!n) {
                if (this.$refs.nameInput) this.$refs.nameInput.focus();
                return;
            }
            this.nameSkipped = false;
            this.meta.nameSkipped = false;
            this.answer("name", n);
        },
        skipName() {
            this.$delete(this.answers, "name");
            this.nameSkipped = true;
            this.meta.nameSkipped = true;
            if (!this.asked.includes("name")) this.asked.push("name");
            this.persist();
            this.advance();
        },
        submitEmail() {
            const v = this.email.trim();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
                this.emailError = this.t("errors.email_invalid");
                if (this.$refs.emailInput) this.$refs.emailInput.focus();
                return;
            }
            this.emailError = "";
            const written = writtenName(this.fullName);
            const n = cleanName(written);
            if (n) {
                this.$set(this.answers, "name", n);
                this.meta.fullName = written;
                this.nameSkipped = false;
            } else {
                this.nameSkipped = true;
            }
            this.emailUnlocked = true;

            if (!this.asked.includes("email")) this.asked.push("email");
            this.persist();
            this.captureLead();
            this.showLoading();
        },
        continueAfterEmail() {
            this.currentStep = "result";
            this.scrollTop();
        },
        claimDiscount() {
            this.currentStep = "email";
            this.scrollTop();
            this.$nextTick(() => {
                const field = this.fullName ? this.$refs.emailInput : this.$refs.nameInput;
                if (field) field.focus({ preventScroll: true });
            });
        },
        clearStoredPromo() {
            try { sessionStorage.removeItem("quizPromocode"); } catch (e) { /* storage unavailable */ }
        },
        persistPromoForCheckout() {
            if (!this.promoCode) return;
            try {
                sessionStorage.setItem(
                    "quizPromocode",
                    JSON.stringify({
                        promocode: this.promoCode,
                        tier: this.promoTier,
                        at: Date.now()
                    })
                );
            } catch (e) {
                /* storage unavailable */
            }
        },
        syncLeadSnapshot() {
            const st = this.engine ? this.engine.computeState(this.answers) : null;
            const sku = st && st.top && this.engine ? this.engine.pickSku(st.top, this.answers) : null;
            const partial = {
                email: this.email.trim(),
                marketing_opt_in: this.marketingOptIn,
                origin_src: this.meta.originSrc,
                days: (this.answers.days || []).join(","),
                days_any: !!this.meta.daysAny,
                budget: this.answers.budget,
                budget_min: this.answers.budgetMin,
                seat: this.answers.seat,
                view: this.answers.view,
                festa: this.answers.festa,
                crew: this.answers.crew,
                party_size: this.answers.partySize,
                box: this.answers.box,
                camarote: this.answers.camarote,
                position: this.answers.position,
                position_secs: (this.answers.positionSecs || []).join(","),
                predicted: st && st.top,
                name_skipped: this.nameSkipped
            };
            if (this.answers.origin) partial.origin = this.answers.origin;
            if (!this.nameSkipped && (this.meta.fullName || this.fullName)) {
                partial.full_name = this.meta.fullName || writtenName(this.fullName);
            }
            if (sku) {
                partial.rec_day = sku.day;
                partial.rec_sector = sku.sec;
                if (sku.row) partial.rec_row = sku.row;
                partial.rec_price = sku.p;
                partial.rec_conf = Math.round(st.conf * 100);
                if (sku.skuId) partial.sku = String(sku.skuId);
            }
            if (this.promoCode) partial.promo_code = this.promoCode;
            const ticket = this.findResultTicket();
            Object.assign(partial, quizBookAnswers({
                bookUrl: this.result && this.result.sku ? this.buildResultUrl() : "",
                ticketHash: ticket && ticket.hash,
                domain: typeof window !== "undefined" && window.location ? window.location.hostname : ""
            }));
            const next = Object.assign({}, this.leadSnapshot);
            Object.keys(partial).forEach(k => {
                if (partial[k] !== undefined && partial[k] !== null && partial[k] !== "") {
                    next[k] = partial[k];
                }
            });
            if (!partial.sku) delete next.sku;
            this.leadSnapshot = next;
        },
        captureLead() {
            this.syncLeadSnapshot();
            this.submitLead();
        },
        async submitLead() {
            if (!this.leadSnapshot.email) return;
            const answers = Object.assign({}, this.leadSnapshot);
            const firstSend = !this.leadSent;
            try {
                const { status } = await submitQuizLead(this.olsBase, {
                    lang: this.lang,
                    answers: answers,
                    utm: this.getUtms()
                });
                if (status === 201) {
                    this.leadSent = true;
                    if (firstSend) {
                        trackGenerateLead("ticket_quiz");
                        pushDataLayer(Object.assign({
                            event: "quiz_completed",
                            event_id: createEventId("quiz")
                        }, this.quizAnalyticsPayload(true)));
                    }
                }
            } catch (e) {
                // The next update sends the full snapshot again. A failed call does not clear the lead.
            }
        },
        showLoading() {
            this.currentStep = "loading";
            this.loadingStep = 0;
            this.clearTimers();
            const d = 520;
            this.loadingTimers.push(setTimeout(() => { this.loadingStep = 1; }, d * 0.6));
            this.loadingTimers.push(setTimeout(() => { this.loadingStep = 2; }, d * 1.5));
            this.loadingTimers.push(setTimeout(() => { this.loadingStep = 3; }, d * 2.4));
            const minDelay = new Promise((resolve) => {
                this.loadingTimers.push(setTimeout(resolve, d * 3.2));
            });
            const ready = this.computeResult();
            Promise.all([minDelay, ready]).then(([, reveal]) => {
                if (reveal === false || this.currentStep !== "loading") return;
                this.currentStep = "result";
                this.scrollTop();
            });
            this.scrollTop();
        },
        computeResult() {
            const st = this.engine.computeState(this.answers);
            if (!st.top) {
                this.result = null;
                this.currentStep = "error";
                return Promise.resolve(false);
            }
            const sku = this.engine.pickSku(st.top, this.answers);
            this.result = {
                k: st.top,
                sku: sku || { day: "", k: st.top, sec: "", row: null, p: 0 },
                match: Math.round(st.conf * 100),
                conf: st.conf,
                state: st
            };
            this.rememberQuizPick();
            this.offer = null;
            this.promoCode = null;
            this.promoTier = 0;
            this.offerStatus = this.emailUnlocked ? "pending" : "none";
            if (this.emailUnlocked) {
                this.captureLead();
                return this.resolveOffer();
            }
            return Promise.resolve(true);
        },
        /** The portal reads this cookie on the next page and puts the ticket at the top of the list. */
        rememberQuizPick() {
            const ticket = this.findResultTicket();
            const hash = ticket && ticket.hash;
            if (!hash || !/^[A-Za-z0-9]{8,64}$/.test(String(hash))) return;
            try {
                document.cookie = `ticket_quiz_pick=${hash}; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
            } catch (e) {
                /* cookies unavailable */
            }
        },
        resultTicketIds() {
            const sku = this.result && this.result.sku;
            const ids = [];
            const push = (id) => {
                if (id == null || id === "") return;
                if (!ids.some(existing => String(existing) === String(id))) ids.push(id);
            };
            if (sku) {
                push(sku.skuId);
                push(sku.id);
            }
            const ticket = this.findResultTicket();
            if (ticket) {
                push(ticket.sku_id);
                push(ticket.id);
            }
            return ids;
        },
        findResultTicket() {
            if (!this.result || !this.result.sku) return null;
            const sku = this.result.sku;
            const slug = this.productSlugs[sku.k] || "";
            return (this.tickets || []).find(t => {
                const tSlug = t.location_slug || t.slug || t.type || "";
                const tDate = String(t.small_date || t.date || t.day || "").slice(0, 10);
                const sectorOk = String(t.sector) === String(sku.sec);
                const dateOk = tDate === sku.day;
                const slugOk = tSlug === slug;
                const rowOk = !sku.row || String(t.row || t.rows || "") === String(sku.row);
                return slugOk && dateOk && sectorOk && rowOk;
            }) || (this.tickets || []).find(t => {
                const tSlug = t.location_slug || t.slug || t.type || "";
                const tDate = String(t.small_date || t.date || t.day || "").slice(0, 10);
                return tSlug === slug && tDate === sku.day && String(t.sector) === String(sku.sec);
            }) || null;
        },
        async resolveOffer() {
            const token = ++this.offerToken;
            this.offer = null;
            this.offerStatus = "pending";
            const ids = this.resultTicketIds();
            const candidates = [];
            const isCamaroteKind =
                this.result &&
                (this.result.k === "folia" || this.result.k === "mar");
            const code20 = getPromoCode(20, this.promoCodes);
            // Folia / Camarote Mar: commercial ladder max 5%. Prefer 5% first so
            // soft-fail (shop down) never unlocks a fake 20% on those products.
            if (isCamaroteKind) {
                const code5 = getPromoCode(5, this.promoCodes);
                if (code5) candidates.push({ code: code5, pct: 5 });
                if (code20 && code20 !== code5) {
                    candidates.push({ code: code20, pct: 20 });
                }
            } else if (code20) {
                candidates.push({ code: code20, pct: 20 });
            }
            if (!ids.length || !candidates.length) {
                if (token === this.offerToken) {
                    this.offerStatus = "none";
                    this.clearStoredPromo();
                    return true;
                }
                return false;
            }
            let softCandidate = null;
            for (let i = 0; i < candidates.length; i++) {
                const candidate = candidates[i];
                try {
                    const res = await validatePromo(candidate.code, { site: this.promoSite });
                    if (token !== this.offerToken) return false;
                    const allowed = res && res.data && res.data.tickets;
                    if (res && res.ok && promoCoversTicket(ids, allowed)) {
                        this.offer = candidate;
                        this.promoTier = candidate.pct;
                        this.promoCode = candidate.code;
                        this.persistPromoForCheckout();
                        this.offerStatus = "ready";
                        this.captureLead();
                        return true;
                    }
                    // Shop unreachable / misconfigured (local) — keep first candidate for soft fail.
                    // Never soft-unlock 20% on Folia/Mar (commercial max 5%).
                    if (
                        this.promoSoftFail &&
                        !softCandidate &&
                        res &&
                        !res.ok &&
                        !(isCamaroteKind && candidate.pct === 20)
                    ) {
                        softCandidate = candidate;
                    }
                } catch (e) {
                    if (token !== this.offerToken) return false;
                    if (
                        this.promoSoftFail &&
                        !softCandidate &&
                        !(isCamaroteKind && candidate.pct === 20)
                    ) {
                        softCandidate = candidate;
                    }
                }
            }
            if (token === this.offerToken && softCandidate) {
                this.offer = softCandidate;
                this.promoTier = softCandidate.pct;
                this.promoCode = softCandidate.code;
                this.persistPromoForCheckout();
                this.offerStatus = "ready";
                this.captureLead();
                return true;
            }
            if (token === this.offerToken) {
                this.offerStatus = "none";
                this.clearStoredPromo();
                return true;
            }
            return false;
        },
        buildReasons(k, a, sku) {
            const out = [];
            const relaxed = (this.result && this.result.state && this.result.state.relaxed) || [];
            const push = (title, sub) => {
                if (!title || title.indexOf("questions.") === 0 || title.indexOf("result.") === 0) return;
                out.push({ title: title, sub: sub && sub.indexOf("questions.") !== 0 && sub.indexOf("result.") !== 0 ? sub : "" });
            };
            if (a.seat && a.seat !== "tanto" && !relaxed.includes("seat")) {
                push(
                    this.t("questions.seat.options." + a.seat + ".label"),
                    this.t("questions.seat.options." + a.seat + ".sub")
                );
            }
            if (a.festa && a.festa !== "tanto" && !relaxed.includes("festa")) {
                push(
                    this.t("questions.festa.options." + a.festa + ".label"),
                    a.festa === "inclusa" ? this.t("result.camarote_includes") : this.t("questions.festa.options." + a.festa + ".sub")
                );
            }
            if (a.view && a.view !== "tanto") {
                push(
                    this.t("questions.view.options." + a.view + ".label"),
                    this.t("questions.view.options." + a.view + ".sub")
                );
            }
            if (sku && sku.day) {
                const night = this.t("result.night_fit");
                const when = [this.resultGroupName, this.resultDateShort].filter(Boolean).join(" · ");
                push(night === "result.night_fit" ? this.t("result.night_title") : night, when);
            }
            if (sku && sku.sec) {
                push(this.sectorLabel, this.resultNightNote);
            }
            return out.slice(0, 4);
        },
        refineMore() {
            this.refine = true;
            this.currentStep = null;
            this.$nextTick(() => this.advance());
        },
        buildResultUrl() {
            if (!this.result || !this.result.sku) return "#";
            const sku = this.result.sku;
            const slug = this.productSlugs[sku.k] || "";

            const ticket = this.findResultTicket();

            const base = window.location.origin;
            const route = (this.page && this.page.route) || window.location.pathname;
            let cleanRoute = route.startsWith("/") ? route.substring(1) : route;
            cleanRoute = cleanRoute.replace(/\/$/, "");
            // Drop trailing ticket hash if already on a details URL
            const parts = cleanRoute.split("/");
            if (parts.length > 1 && /^[a-f0-9]{8,}$/i.test(parts[parts.length - 1])) {
                parts.pop();
                cleanRoute = parts.join("/");
            }

            let url;
            if (ticket && ticket.hash) {
                url = new URL(`${base}/${cleanRoute}/${ticket.hash}`);
            } else {
                url = new URL(`${base}/${cleanRoute}`);
                if (sku.day) url.searchParams.set("day", sku.day);
                if (sku.sec) url.searchParams.set("sector", sku.sec);
                if (sku.row) url.searchParams.set("row", sku.row);
                if (slug) url.searchParams.set("type", slug);
            }
            if (this.promoCode) url.searchParams.set("promocode", this.promoCode);
            const utms = this.getUtms();
            Object.keys(utms).forEach(k => url.searchParams.set(k, utms[k]));
            return url.toString();
        },
        quizAnalyticsPayload(includeItems) {
            const answers = this.answers || {};
            const result = this.result;
            const sku = result && result.sku;
            const clip = (value) => {
                if (value == null || value === "") return "";
                const text = Array.isArray(value) ? value.map(String).join(",") : String(value);
                return text.slice(0, 200);
            };
            const payload = { quiz_name: "ticket_quiz" };
            const fields = {
                quiz_origin: answers.origin,
                quiz_days: answers.days,
                quiz_budget: answers.budget,
                quiz_seat: answers.seat,
                quiz_view: answers.view,
                quiz_festa: answers.festa,
                quiz_crew: answers.crew,
                quiz_party_size: answers.partySize,
                quiz_box: answers.box,
                quiz_camarote: answers.camarote,
                quiz_position: answers.position,
                quiz_position_secs: answers.positionSecs,
                quiz_product: result && result.k,
                quiz_sector: sku && sku.sec,
                quiz_day: sku && sku.day,
                quiz_row: sku && sku.row,
                quiz_sku: sku && (sku.skuId || sku.id),
                quiz_match: result && result.match
            };
            Object.keys(fields).forEach((key) => {
                const value = clip(fields[key]);
                if (value) payload[key] = value;
            });
            if (includeItems) {
                const items = this.quizAnalyticsItems();
                if (items) payload.items = items;
            }
            return payload;
        },
        quizAnalyticsItems() {
            const ticket = this.findResultTicket();
            const sku = this.result && this.result.sku;
            const itemId = (ticket && (ticket.sku_id || ticket.id)) || (sku && (sku.skuId || sku.id));
            if (itemId == null || itemId === "") return null;
            const item = { item_id: String(itemId).slice(0, 200) };
            const category = (ticket && (ticket.location_slug || ticket.slug || ticket.type)) || (sku && sku.sec);
            if (category) item.item_category = String(category).slice(0, 200);
            if (this.result && this.result.k) item.item_name = String(this.result.k).slice(0, 200);
            return [item];
        },
        navigateToResult() {
            if (this.result && this.result.k) {
                pushDataLayer(Object.assign({
                    event: "quiz_cta_product",
                    event_id: createEventId("quiz")
                }, this.quizAnalyticsPayload(true)));
            }
            if (this.promoCode) this.persistPromoForCheckout();
            else this.clearStoredPromo();
            window.location.href = this.buildResultUrl();
        },
        getUtms() {
            const params = new URLSearchParams(window.location.search);
            const utms = {};
            ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach(k => {
                const v = params.get(k);
                if (v) utms[k] = v;
            });
            if (!utms.utm_source) utms.utm_source = "quiz";
            if (!utms.utm_medium) utms.utm_medium = "recomendador";
            return utms;
        },
        async copyCode() {
            if (!this.promoCode) return;
            try {
                await navigator.clipboard.writeText(this.promoCode);
                this.copiedCode = true;
                setTimeout(() => { this.copiedCode = false; }, 3000);
            } catch (e) {
                this.copiedCode = false;
            }
        },
        close() {
            this.$emit("close");
        },
        scrollTop() {
            this.$nextTick(() => {
                if (this.$refs.body) this.$refs.body.scrollTop = 0;
            });
        },
        clearTimers() {
            this.loadingTimers.forEach(t => clearTimeout(t));
            this.loadingTimers = [];
        }
    },
    beforeDestroy() {
        this.unbindKeydown();
        this.clearTimers();
    }
};
</script>

<style lang="scss" scoped>
$quiz-purple: #7b2bff;
$quiz-purple-deep: #6f00e8;
$quiz-purple-soft: #f3ecff;
$quiz-purple-border: #d9c2ff;
$quiz-navy: #121a4f;
$quiz-text: #1b2147;
$quiz-muted: #6e7595;
$quiz-border: #e8e5f4;
$quiz-bg: #ffffff;
$quiz-surface: #faf8ff;
$quiz-ease: cubic-bezier(0.32, 0.72, 0, 1);

.quiz-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px;
    background: rgba(0, 0, 0, 0.45);
    -webkit-overflow-scrolling: touch;

    @media (max-width: 699px) {
        padding: 0;
    }
}

.quiz-modal {
    position: relative;
    width: 100%;
    max-width: 640px;
    max-height: calc(100vh - 44px);
    height: auto;
    border-radius: 30px;
    background: $quiz-bg;
    box-shadow: 0 30px 90px rgba(20, 13, 54, 0.22);
    overflow: hidden;

    &--split {
        max-width: 960px;
    }

    &--form {
        max-width: 520px;
    }

    @media (max-width: 699px) {
        max-width: 100%;
        max-height: 100%;
        height: 100%;
        border-radius: 0;
    }
}

.quiz-modal--split .quiz-modal__header {
    position: absolute;
    inset: 0 0 auto 0;
    border-bottom: 0;
    background: transparent;
    pointer-events: none;
    z-index: 3;
}

.quiz-modal--split .quiz-modal__close,
.quiz-modal--split .quiz-modal__bar {
    pointer-events: auto;
}

.quiz-modal--split .quiz-modal__bar {
    position: absolute;
    top: 22px;
    left: calc(32% + 80px);
    right: 76px;
    padding-right: 0;
}

.quiz-modal--split.quiz-modal--days .quiz-modal__bar {
    left: 28px;
}

@media (max-width: 959px) {
    .quiz-modal--split.quiz-modal--days .quiz-modal__header {
        position: relative;
        inset: auto;
        border-bottom: 1px solid $quiz-border;
        background: $quiz-bg;
        pointer-events: auto;
    }

    .quiz-modal--split.quiz-modal--days .quiz-modal__bar {
        position: static;
        padding-right: 54px;
    }

    .quiz-intro__content.quiz-days {
        padding-top: 20px;
    }
}

.quiz-modal--split .quiz-modal__frame {
    height: auto;
    max-height: calc(100vh - 46px);
}

.quiz-modal--split .quiz-modal__body {
    flex: 0 0 auto;
    overflow: hidden;
    max-height: none;
    padding: 0;
}

.quiz-modal__frame {
    display: flex;
    flex-direction: column;
    height: auto;
    max-height: calc(100vh - 46px);
    border-radius: inherit;
    background: $quiz-bg;

    @media (max-width: 699px) {
        height: 100%;
        max-height: 100%;
        border-radius: 0;
    }
}

.quiz-modal__header {
    position: relative;
    flex-shrink: 0;
    padding: 24px 28px 18px;
    border-bottom: 1px solid $quiz-border;
    background: $quiz-bg;

    @media (max-width: 699px) {
        padding: 18px 18px 16px;
    }
}

.quiz-modal__close,
.quiz-modal__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid $quiz-border;
    border-radius: 999px;
    background: #f3f3f6;
    color: #7e839e;
    cursor: pointer;
    appearance: none;
    box-shadow: 0 8px 20px rgba(35, 30, 82, 0.08);
    transition: transform 0.32s $quiz-ease, opacity 0.32s $quiz-ease;

    &:hover {
        transform: translateY(-1px);
    }

    &:active {
        transform: scale(0.97);
    }

    &:focus {
        outline: none;
        box-shadow:
            0 0 0 3px rgba(123, 43, 255, 0.16),
            0 8px 20px rgba(35, 30, 82, 0.08);
    }
}

.quiz-modal__close,
.quiz-modal__back {
    position: absolute;
    top: 18px;
    z-index: 2;
    margin: 0;

    @media (max-width: 699px) {
        top: 14px;
    }
}

.quiz-modal__close {
    right: 24px;

    @media (max-width: 699px) {
        right: 14px;
    }
}

.quiz-modal__back {
    left: 24px;
    color: $quiz-purple;
    background: #f8f4ff;

    @media (max-width: 699px) {
        left: 14px;
    }
}

.quiz-modal__bar {
    padding-right: 64px;

    @media (max-width: 699px) {
        padding-right: 54px;
    }
}

.quiz-modal__bar--back {
    padding-left: 64px;

    @media (max-width: 699px) {
        padding-left: 54px;
    }
}

.quiz-modal__progress {
    position: relative;
    height: 8px;
    border-radius: 999px;
    background: #eee9fb;
    overflow: hidden;
}

.quiz-modal__progress-fill {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    transform-origin: left center;
    background: $quiz-purple;
    transition: transform 0.7s $quiz-ease;
}

.quiz-modal__bar-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $quiz-muted;

    @media (max-width: 699px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        letter-spacing: 0.06em;
    }
}

.quiz-modal__bar-disc {
    font-weight: 700;
    color: $quiz-purple;
}

.quiz-modal__body {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 24px 28px 30px;

    @media (max-width: 699px) {
        padding: 14px 16px 22px;
    }
}

.quiz-screen {
    width: 100%;
}

.quiz-screen--intro,
.quiz-screen--days {
    max-width: none;
    padding: 0;
}

.quiz-split {
    display: grid;
    grid-template-columns: minmax(0, 0.32fr) minmax(0, 0.68fr);
    align-items: stretch;

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }
}

.quiz-screen--days .quiz-split {
    grid-template-columns: minmax(0, 1fr);
}

.quiz-screen--loading {
    max-width: 720px;
}

.quiz-screen--result {
    max-width: none;
}

.quiz-intro {
    display: grid;
    grid-template-columns: minmax(0, 0.32fr) minmax(0, 0.68fr);
    align-items: stretch;

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }
}

.quiz-intro__media {
    position: relative;
    z-index: 2;
    // Content column drives row height; image must not contribute intrinsic size.
    min-height: 0;
    min-width: 0;
    margin-right: -48px;
    overflow: hidden;
    background: #1a1028;
    // Soft rounded right edge (bulges into the white panel) — not a pointed ellipse tip.
    border-radius: 0 88px 88px 0;

    @media (max-width: 699px) {
        z-index: 1;
        min-height: 180px;
        margin-right: 0;
        border-radius: 0;
    }
}

.quiz-intro__hero {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
}

.quiz-intro__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    position: relative;
    z-index: 1;
    margin-left: 0;
    padding: 40px 48px 36px 72px;
    background: $quiz-bg;
    border-radius: 0;

    @media (max-width: 699px) {
        padding: 28px 20px 28px;
        border-radius: 28px 28px 0 0;
        margin-top: -20px;
    }
}

.quiz-screen__title {
    margin: 0 0 12px;
    font-size: 36px;
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.03em;
    color: $quiz-navy;

    @media (max-width: 699px) {
        font-size: 30px;
    }
}

.quiz-screen__title--intro {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 440px;
}

.quiz-screen__title-lead,
.quiz-screen__title-tail {
    color: $quiz-navy;
}

.quiz-screen__title-line {
    display: inline;
}

.quiz-screen__title-accent {
    color: $quiz-purple;
}

.quiz-screen__lead,
.quiz-screen__help {
    margin: 0 0 18px;
    font-size: 15px;
    line-height: 1.65;
    color: $quiz-muted;
}

.quiz-q-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 14px;
}

.quiz-q-count {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: $quiz-purple;
}

.quiz-q-hint {
    font-size: 11px;
    color: #8a90ad;
    font-style: normal;
}

.quiz-reward,
.quiz-confirm-card,
.quiz-trend,
.quiz-refine,
.quiz-night,
.quiz-why {
    padding: 16px 18px;
    border-radius: 20px;
    background: $quiz-bg;
    border: 1px solid $quiz-border;
    box-shadow: 0 12px 28px rgba(34, 28, 78, 0.06);
}

.quiz-reward {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 18px 0 22px;
    padding: 18px 20px;
    border: 0;
    border-radius: 16px;
    background: #f3e8ff;
    box-shadow: none;

    @media (max-width: 699px) {
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
    }
}

.quiz-reward__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
}

.quiz-reward__koko {
    display: block;
    width: 60px;
    height: auto;
    // Original asset points left; flip so Koko gestures toward the discount copy.
    transform: scaleX(-1);
}

.quiz-reward__divider {
    flex: 0 0 auto;
    width: 1px;
    align-self: stretch;
    margin: 4px 0;
    background: rgba(111, 0, 232, 0.22);

    @media (max-width: 699px) {
        display: none;
    }
}

.quiz-reward__copy {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.quiz-reward__eyebrow {
    display: block;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $quiz-purple-deep;
}

.quiz-reward__value {
    display: block;
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
    text-transform: uppercase;
    color: $quiz-purple-deep;
}

.quiz-reward p {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: #3d3a4a;
}

.quiz-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 50px;
    padding: 13px 22px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    appearance: none;
    text-decoration: none;
    text-align: center;
    transition: transform 0.32s $quiz-ease, opacity 0.32s $quiz-ease;

    &:hover {
        transform: translateY(-1px);
    }

    &:active {
        transform: scale(0.98);
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(123, 43, 255, 0.16);
    }

    &--primary,
    &--buy {
        background: $quiz-purple;
        color: #fff;
        box-shadow: 0 16px 34px rgba(111, 0, 232, 0.28);
    }

    &--ghost {
        margin-top: 10px;
        background: $quiz-purple-soft;
        border-color: transparent;
        color: $quiz-navy;
        box-shadow: none;
    }

    &--link {
        margin-top: 12px;
        min-height: 34px;
        padding: 2px 6px;
        background: none;
        color: $quiz-purple;
        font-weight: 600;
        font-size: 13px;
        width: auto;
    }

    &--buy {
        flex-direction: column;
        gap: 6px;
        padding: 16px 24px;

        b {
            font-size: 15px;
        }

        span {
            font-size: 12px;
            opacity: 0.82;
            font-weight: 500;
        }
    }

    &--small {
        width: auto;
        min-height: 38px;
        padding: 8px 16px;
        font-size: 13px;
    }
}

.quiz-btn__arrow {
    position: absolute;
    right: 8px;
    top: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24);
    transform: translateY(-50%);
}

.quiz-btn--with-arrow {
    position: relative;
    justify-content: center;
    padding-left: 48px;
    padding-right: 48px;
    font-size: 18px;
}

.quiz-note {
    margin: 14px 0 0;
    font-size: 12px;
    line-height: 1.6;
    color: #8a90ad;
    text-align: center;
}

.quiz-origin-grid {
    display: grid;
    gap: 10px;
    margin-bottom: 18px;
}

.quiz-origin-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }
}

.quiz-origin-cell,
.quiz-opt,
.quiz-day-card {
    position: relative;
    overflow: hidden;
    padding: 14px 16px;
    border: 1px solid $quiz-border;
    border-radius: 18px;
    background: $quiz-bg;
    text-align: left;
    color: $quiz-text;
    cursor: pointer;
    appearance: none;
    box-shadow: 0 10px 22px rgba(34, 28, 78, 0.04);
    transition: transform 0.32s $quiz-ease, opacity 0.32s $quiz-ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: #ccb0ff;
    }
}

.quiz-origin-cell {
    font-size: 14px;
    font-weight: 600;

    &:hover,
    &.picked {
        border-color: $quiz-purple;
        box-shadow: 0 0 0 3px rgba(123, 43, 255, 0.08);
    }

    &.picked {
        background: #fbf8ff;
    }
}

.quiz-confirm-q {
    margin: 0 0 8px;
    font-size: 18px;
    line-height: 1.45;
    color: $quiz-navy;
}

.quiz-confirm-why {
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 1.6;
    color: $quiz-muted;
}

.quiz-confirm-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.quiz-intro__content.quiz-days {
    width: 100%;
    padding: 64px 28px 20px;
}

.quiz-days .quiz-screen__help {
    margin-bottom: 12px;
}

.quiz-days__title {
    display: block;
    max-width: none;
    margin-bottom: 8px;
    font-size: 28px;
    white-space: nowrap;

    @media (max-width: 959px) {
        font-size: 24px;
        white-space: normal;
    }
}

.quiz-days__board {
    display: grid;
    grid-template-columns: auto auto minmax(236px, 1fr);
    grid-template-rows: auto auto;
    align-items: start;
    margin-top: 4px;
}

.quiz-days__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid #eee9f6;

    .quiz-btn--link {
        width: auto;
        margin: 0;
        text-decoration: underline;
    }
}

.quiz-days__actions {
    display: flex;
    gap: 10px;
    margin: 0;
    flex: 0 0 auto;

    .quiz-btn {
        width: auto;
        min-width: 120px;
    }

    .quiz-btn--ghost {
        margin-top: 0;
    }
}

.quiz-day-group {
    display: contents;
}

.quiz-day-group__header,
.quiz-days .quiz-day-grid {
    min-width: 0;
    padding: 0 8px;
    border-left: 1px solid #eee6f4;
}

.quiz-day-group:first-child .quiz-day-group__header,
.quiz-day-group:first-child .quiz-day-grid {
    padding-left: 0;
    border-left: 0;
}

.quiz-day-group:last-child .quiz-day-group__header,
.quiz-day-group:last-child .quiz-day-grid {
    padding-right: 0;
}

.quiz-day-group__header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    grid-row: 1;
    margin-bottom: 12px;

    strong {
        display: block;
        font-size: 15px;
        color: $quiz-navy;
    }
}

.quiz-day-group__copy span {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    line-height: 1.4;
    color: $quiz-muted;
}

.quiz-day-group__icon {
    flex: 0 0 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin: 0;
    border-radius: 50%;
    line-height: 0;

    svg {
        display: block;
        width: 26px;
        height: 26px;
        flex: 0 0 26px;
    }

    &--ouro {
        background: #fbf4e4;
        color: #b8862a;
    }

    &--especial {
        background: #ffe8f2;
        color: #e23b8b;
    }

    &--campeas {
        background: #f3ecff;
        color: $quiz-purple-deep;
    }
}

.quiz-days .quiz-day-grid {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    gap: 8px;
    grid-row: 2;
    align-self: start;
    margin: 0;
}

@media (max-width: 959px) {
    .quiz-days__board {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .quiz-day-group {
        display: flex;
        flex-direction: column;
    }

    .quiz-day-group__header,
    .quiz-days .quiz-day-grid {
        border-left: 0;
        padding: 0;
    }

    .quiz-day-group__header {
        justify-content: flex-start;
        margin-bottom: 10px;
    }

    .quiz-days .quiz-day-grid {
        justify-content: flex-start;
    }

}

@media (max-width: 699px) {

    .quiz-days__foot {
        flex-direction: column-reverse;
        align-items: stretch;

        .quiz-btn--link {
            align-self: center;
        }
    }

    .quiz-days__actions .quiz-btn {
        flex: 1 1 0;
        min-width: 0;
    }
}

.quiz-days .quiz-day-card {
    display: flex;
    flex: 0 0 108px;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 0;
    width: 108px;
    max-width: 108px;
    padding: 10px 4px 8px;
    text-align: center;

    &:hover:not(:disabled),
    &.selected {
        border-color: $quiz-purple;
        box-shadow: 0 0 0 3px rgba(123, 43, 255, 0.08);
    }

    &.selected {
        background: #fbf8ff;
    }

    &.soldout {
        opacity: 0.42;
        cursor: not-allowed;
    }

    &__box {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        margin-top: 10px;
        border: 1px solid $quiz-border;
        border-radius: 7px;
        background: #fff center / 12px 12px no-repeat;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
        transition: border-color 0.2s $quiz-ease, background-color 0.2s $quiz-ease;
    }

    &__month,
    &__weekday {
        font-size: 11px;
        font-weight: 700;
        line-height: 1.1;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #9a90b5;
    }

    &__day {
        margin: 2px 0 1px;
        font-size: 28px;
        font-weight: 800;
        line-height: 0.95;
        letter-spacing: -0.03em;
        color: $quiz-navy;
    }
}

.quiz-slider {
    margin: 22px 0 24px;

    &__value {
        margin-bottom: 20px;
        text-align: center;

        strong {
            font-size: 34px;
            font-weight: 800;
            letter-spacing: -0.04em;
            color: $quiz-navy;
        }

        span {
            display: block;
            margin-top: 4px;
            font-size: 12px;
            color: $quiz-muted;
        }
    }

    &__scale {
        display: flex;
        justify-content: space-between;
        margin-top: 10px;
        font-size: 12px;
        color: #8a90ad;
    }
}

.quiz-range {
    position: relative;
    height: 28px;
    touch-action: none;
    cursor: pointer;

    &__track {
        position: absolute;
        top: 10px;
        right: 12px;
        left: 12px;
        height: 8px;
        border-radius: 999px;
        background: #eee9fb;
    }

    &__fill {
        position: absolute;
        top: 0;
        bottom: 0;
        border-radius: 999px;
        background: $quiz-purple;
    }

    &__input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 28px;
        margin: 0;
        opacity: 0;
        pointer-events: none;
        appearance: none;
    }

    &__thumb {
        position: absolute;
        top: 2px;
        z-index: 3;
        width: 24px;
        height: 24px;
        margin-left: -12px;
        border: 3px solid #fff;
        border-radius: 50%;
        background: $quiz-purple;
        box-shadow: 0 2px 6px rgba(111, 0, 232, 0.25);
        pointer-events: none;
        transition: box-shadow 0.2s $quiz-ease;

        &.is-active {
            z-index: 4;
        }
    }

    &__input--min:focus-visible ~ &__thumb--min,
    &__input--max:focus-visible ~ &__thumb--max {
        box-shadow:
            0 2px 6px rgba(111, 0, 232, 0.25),
            0 0 0 4px rgba(123, 43, 255, 0.2);
    }
}

.quiz-opts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 16px;

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }
}

.quiz-opts .quiz-opt:last-child:nth-child(odd) {
    @media (min-width: 700px) {
        grid-column: 1 / -1;
        width: calc(50% - 5px);
        justify-self: center;
    }
}

.quiz-opts--art {
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }

    .quiz-opt {
        flex-direction: row;
        align-items: center;
        min-height: 0;
        padding: 6px 10px 6px 4px;
        gap: 6px;
    }
}

.quiz-opt__art {
    flex: 0 0 118px;
    width: 118px;
    height: 78px;
    object-fit: contain;
    object-position: center;
    mix-blend-mode: multiply;
    pointer-events: none;
}

.quiz-opts .quiz-opt {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    min-height: 128px;
    padding: 16px 14px 14px;
    border-radius: 22px;
    text-align: left;
    transition: transform 0.45s $quiz-ease, border-color 0.45s $quiz-ease, box-shadow 0.45s $quiz-ease;

    &:hover:not(:disabled) {
        transform: translateY(-3px);
        border-color: #c9a8ff;
        box-shadow: 0 14px 28px rgba(111, 0, 232, 0.08);
    }

    &:active {
        transform: scale(0.98);
    }

    &.picked,
    &:hover,
    &.picked {
        border-color: $quiz-purple;
    }

    &.picked {
        background: #fbf8ff;
        box-shadow: 0 0 0 3px rgba(123, 43, 255, 0.1);
    }
}

.quiz-opts.quiz-opts--art .quiz-opt {
    flex-direction: row;
    align-items: center;
    min-height: 0;
    padding: 8px 18px 8px 6px;
    gap: 6px;
}

.quiz-opts.quiz-opts--art .quiz-opt__art {
    flex: 0 0 84px;
    width: 84px;
    height: 64px;
}

.quiz-opts.quiz-opts--art .quiz-opt__label {
    font-size: 15px;
    font-weight: 800;
    letter-spacing: -0.02em;
}

.quiz-opt__mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 14px;
    background: #f4f0fb;
    color: $quiz-purple-deep;

    ::v-deep svg {
        display: block;
        width: 22px;
        height: 22px;
    }

    &--solo,
    &--marcado,
    &--center { background: #f3ecff; color: #6f00e8; }
    &--casal,
    &--folia,
    &--drum1,
    &--drum2 { background: #ffe8f2; color: #d42b78; }
    &--amigos,
    &--inclusa,
    &--start { background: #fff3d8; color: #b8862a; }
    &--criancas,
    &--local,
    &--livre { background: #e7f8ef; color: #1f8a56; }
    &--idosos,
    &--alto,
    &--end { background: #e8f1ff; color: #2d62d6; }
    &--perto,
    &--desfile { background: #f3ecff; color: #6f00e8; }
    &--mar { background: #e5f7f6; color: #0e8a86; }
    &--tanto { background: #f4f2f8; color: #8a86a0; }
}

.quiz-opt__copy {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.quiz-opt__label {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
    color: $quiz-navy;
}

.quiz-opt__sub {
    font-size: 12px;
    line-height: 1.4;
    color: $quiz-muted;
}

.quiz-trend {
    margin-top: 14px;
    font-size: 13px;
    line-height: 1.55;
    color: $quiz-muted;

    &::before {
        content: "";
        display: inline-block;
        width: 8px;
        height: 8px;
        margin-right: 8px;
        border-radius: 50%;
        background: $quiz-purple;
        vertical-align: middle;
    }
}

.quiz-namebox {
    margin-bottom: 20px;
}

.quiz-input {
    width: 100%;
    padding: 14px 16px;
    border: 1px solid $quiz-border;
    border-radius: 18px;
    background: #fff;
    color: $quiz-text;
    font-size: 15px;
    outline: none;
    box-sizing: border-box;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);

    &::placeholder {
        color: #9ba0b8;
    }

    &:focus {
        border-color: $quiz-purple;
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            0 0 0 3px rgba(123, 43, 255, 0.12);
    }
}

.quiz-name-hi {
    margin-top: 12px;
    font-size: 16px;
    font-weight: 600;
    color: $quiz-purple;
}

.quiz-email-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    margin-top: 6px;
}

.quiz-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.quiz-field__label {
    color: $quiz-navy;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.quiz-email-form .quiz-input {
    min-height: 52px;
    border-radius: 16px;
    background: #fff;
}

.quiz-email-form .quiz-btn {
    margin-top: 2px;
}

.quiz-email-form .quiz-error,
.quiz-email-form .quiz-optin,
.quiz-email-form .quiz-fine-print {
    margin: 0;
}

.quiz-count {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 4px 0 16px;
}

.quiz-count__label {
    margin-right: auto;
    color: $quiz-navy;
    font-size: 15px;
    font-weight: 700;
}

.quiz-count__btn {
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: #f3ecff;
    color: $quiz-navy;
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
}

.quiz-count__value {
    min-width: 1.5ch;
    color: $quiz-navy;
    font-size: 22px;
    font-weight: 800;
    text-align: center;
}

.quiz-email-row {
    display: flex;
    gap: 10px;

    .quiz-input {
        flex: 1;
    }

    .quiz-btn {
        width: auto;
        white-space: nowrap;
    }

    @media (max-width: 699px) {
        flex-direction: column;

        .quiz-btn {
            width: 100%;
        }
    }
}

.quiz-error {
    margin: 10px 0 0;
    font-size: 13px;
    color: #d74e67;
}

.quiz-optin {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 14px 0 8px;
    font-size: 13px;
    line-height: 1.5;
    color: $quiz-muted;
    cursor: pointer;

    align-items: center;
    min-height: 44px;
    font-size: 14px;

    input {
        flex: 0 0 22px;
        width: 22px;
        height: 22px;
        margin: 0;
        border: 1px solid $quiz-border;
        border-radius: 7px;
        background: #fff center / 12px 12px no-repeat;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
        appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
        transition: border-color 0.2s $quiz-ease, background-color 0.2s $quiz-ease, box-shadow 0.2s $quiz-ease;

        &:hover:not(:checked) {
            border-color: #ccb0ff;
        }

        &:focus {
            outline: none;
        }

        &:focus-visible {
            border-color: $quiz-purple;
            box-shadow:
                inset 0 1px 0 rgba(255, 255, 255, 0.8),
                0 0 0 3px rgba(123, 43, 255, 0.12);
        }
    }
}

.quiz-days .quiz-day-card.selected .quiz-day-card__box,
.quiz-optin input:checked {
    border-color: $quiz-purple;
    background-color: $quiz-purple;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2.5 6.2 5 8.5l4.5-5' stroke='%23fff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
}

.quiz-days .quiz-day-card:hover:not(:disabled):not(.selected) .quiz-day-card__box {
    border-color: #ccb0ff;
}

.quiz-fine-print {
    margin: 8px 0 0;
    font-size: 11px;
    line-height: 1.55;
    color: #8a90ad;
}

.quiz-screen--loading {
    text-align: center;
    padding-top: 36px;
}

.quiz-loading-steps {
    margin-top: 32px;
    text-align: left;
}

.quiz-loading-step {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 0;
    font-size: 14px;
    line-height: 1.45;
    color: #9ba0b8;
    transition: opacity 0.32s $quiz-ease, transform 0.32s $quiz-ease;

    &.on {
        color: $quiz-navy;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: #f3ecff;
        font-size: 12px;
        color: #9ba0b8;
    }

    &.on &__icon {
        background: $quiz-purple;
        color: #fff;
    }
}

.quiz-modal--result {
    max-width: 880px;
}

.quiz-modal--result .quiz-modal__header {
    margin-bottom: 8px;
}

.quiz-modal--result .quiz-modal__body {
    padding: 6px 28px 24px;
}

.quiz-modal__bar-match {
    margin-left: auto;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $quiz-navy;
}

.quiz-match {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 210px;
    gap: 12px 20px;
    align-items: center;

    @media (max-width: 699px) {
        grid-template-columns: 1fr;
    }
}

.quiz-match__art {
    display: block;
    width: 210px;
    max-width: 100%;
    height: auto;
    margin-left: auto;
    object-fit: contain;

    @media (max-width: 699px) {
        order: -1;
        width: 100%;
        max-height: 200px;
        margin-left: 0;
    }
}

.quiz-match__eyebrow {
    display: inline-flex;
    margin-bottom: 8px;
    padding: 4px 8px;
    border-radius: 999px;
    background: #f3ecff;
    color: $quiz-purple;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.quiz-match__name {
    margin: 0 0 8px;
    font-size: 32px;
    font-weight: 800;
    line-height: 0.98;
    letter-spacing: -0.04em;
    color: $quiz-navy;
}

.quiz-match__meta {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    margin: 0 0 11px;

    @media (max-width: 699px) {
        flex-wrap: wrap;
    }

    span {
        display: inline-flex;
        align-items: center;
        padding: 6px 10px;
        border-radius: 999px;
        background: #f3ecff;
        color: $quiz-navy;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1;
    }
}

.quiz-match__hello {
    margin: 0 0 10px;
    max-width: 28em;
    color: $quiz-navy;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
}

.quiz-match__desc {
    margin: 0 0 13px;
    max-width: 36em;
    color: $quiz-muted;
    font-size: 12px;
    line-height: 1.5;
}

.quiz-match__facts {
    display: flex;
    gap: 8px;
    margin-bottom: 11px;
}

.quiz-match__price,
.quiz-match__off {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 52px;
    padding: 8px 11px;
    border-radius: 14px;
    background: #f6f3fb;
}

.quiz-match__price strong {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: $quiz-navy;
}

.quiz-match__price span,
.quiz-match__off {
    font-size: 11px;
    font-weight: 700;
    color: $quiz-purple;
}

.quiz-match__total {
    display: block;
    margin-top: 4px;
    color: $quiz-navy;
}

.quiz-match__over {
    margin: 0 0 10px;
    max-width: 36em;
    color: #8a4b08;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
}

.quiz-match__off {
    flex: 1 1 160px;
    min-width: 0;
}

.quiz-match__promo-label {
    display: block;
    margin-bottom: 3px;
    color: $quiz-muted;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 1;
}

.quiz-match__code {
    display: block;
    margin-bottom: 3px;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1.15;
    color: $quiz-navy;
    word-break: break-all;
}

.quiz-match__claim {
    border: 0;
    cursor: pointer;
    text-align: left;
    transition: transform 0.32s $quiz-ease;

    span {
        font-size: 12px;
        font-weight: 700;
        color: $quiz-purple;
    }

    &:hover {
        transform: translateY(-1px);
    }

    &:active {
        transform: scale(0.98);
    }
}

.quiz-match .quiz-btn {
    min-height: 40px;
    margin-top: 3px;
    padding: 10px 18px;
    font-size: 13px;
}

.quiz-match__applied {
    margin: 10px 0 0;
    color: #8a90ad;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
}

.quiz-screen--result .quiz-why {
    margin-top: 18px;
    padding: 18px 18px 8px;
    border-radius: 24px;
    background: #f7f4fc;

    h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 14px;
        font-size: 18px;
        color: $quiz-navy;
    }

    ul {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px 18px;
        margin: 0;
        padding: 0;
        list-style: none;

        @media (max-width: 699px) {
            grid-template-columns: 1fr;
        }
    }

    li {
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }

    strong {
        display: block;
        margin-bottom: 2px;
        color: $quiz-navy;
        font-size: 14px;
    }

    li span:not(.quiz-why__check) {
        display: block;
        color: $quiz-muted;
        font-size: 12px;
        line-height: 1.45;
    }
}

.quiz-why__spark {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: $quiz-purple;
    transform: rotate(45deg);
}

.quiz-why__check {
    display: inline-flex;
    flex: 0 0 22px;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: $quiz-purple;
    color: #fff;
    font-size: 12px;
    font-weight: 800;
}

.quiz-result__footer {
    display: flex;
    justify-content: center;
    gap: 18px;
    margin-top: 8px;

    .quiz-btn--link {
        width: auto;
    }
}

.quiz-result-card {
    margin-bottom: 20px;
    border-radius: 24px;
    border: 1px solid $quiz-border;
    background: $quiz-bg;
    box-shadow: 0 18px 40px rgba(34, 28, 78, 0.08);
    overflow: hidden;

    &__header,
    &__pass {
        position: relative;
        z-index: 1;
    }

    &__header {
        padding: 22px 22px 18px;
        background: #fff;
        color: $quiz-navy;
    }

    &__group {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.14em;
        color: $quiz-purple;
    }

    &__name {
        margin: 6px 0 0;
        font-size: 26px;
        font-weight: 700;
        line-height: 1.08;
        letter-spacing: -0.03em;
    }

    &__pass {
        display: flex;
        margin: 0;
        background: #fff;
        border-top: 1px solid $quiz-border;

        > div {
            flex: 1;
            padding: 16px 18px;
            border-right: 1px solid $quiz-border;

            &:last-child {
                border-right: 0;
            }
        }

        dt {
            margin: 0;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            color: #8a90ad;
        }

        dd {
            margin: 4px 0 0;
            font-size: 17px;
            font-weight: 700;
            color: $quiz-navy;
        }

        small {
            font-size: 11px;
            color: $quiz-muted;
        }

        @media (max-width: 699px) {
            flex-direction: column;

            > div {
                border-right: 0;
                border-bottom: 1px solid $quiz-border;
            }

            > div:last-child {
                border-bottom: 0;
            }
        }
    }

    &__price {
        color: $quiz-purple !important;
    }
}

.quiz-promo {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
    border-radius: 22px;
    background: $quiz-bg;
    border: 1px solid $quiz-border;
    overflow: hidden;

    &--reveal {
        margin-top: 18px;
    }

    &__tier,
    &__code {
        background: #fff;
    }

    &__tier {
        min-width: 118px;
        align-self: stretch;
        padding: 18px 14px;
        border-radius: 0;
        text-align: center;
        background: $quiz-purple-soft;

        strong {
            display: block;
            font-size: 28px;
            font-weight: 800;
            color: $quiz-purple;
        }

        small {
            font-size: 10px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #8a90ad;
        }
    }

    &__code {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        min-width: 0;
        align-self: stretch;
        padding: 18px;
        border-radius: 0;
        border-left: 1px solid $quiz-border;

        b {
            min-width: 0;
            font-size: 18px;
            letter-spacing: 0.14em;
            color: $quiz-navy;
            word-break: break-all;
        }

        button {
            flex-shrink: 0;
            padding: 9px 14px;
            border: 1px solid $quiz-purple-border;
            border-radius: 999px;
            background: #fff;
            color: $quiz-purple;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
            appearance: none;
            white-space: nowrap;
            transition: transform 0.32s $quiz-ease, opacity 0.32s $quiz-ease;

            &:hover {
                transform: translateY(-1px);
            }
        }
    }

    @media (max-width: 699px) {
        flex-direction: column;
        gap: 0;

        &__tier,
        &__code {
            width: 100%;
            border-radius: 0;
        }

        &__code {
            flex-direction: column;
            align-items: flex-start;
            border-left: 0;
            border-top: 1px solid $quiz-border;
        }
    }
}

.quiz-cta-stack {
    margin-bottom: 20px;
}

.quiz-refine {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;

    strong {
        display: block;
        font-size: 14px;
        color: $quiz-navy;
    }

    p {
        margin: 4px 0 0;
        font-size: 12px;
        line-height: 1.55;
        color: $quiz-muted;
    }

    @media (max-width: 699px) {
        flex-direction: column;
        align-items: stretch;
    }
}

.quiz-why,
.quiz-night {
    margin-bottom: 20px;

    h3 {
        margin: 0 0 8px;
        font-size: 15px;
        font-weight: 700;
        color: $quiz-navy;
    }
}

.quiz-why {
    &__desc {
        margin: 0 0 10px;
        font-size: 13px;
        line-height: 1.6;
        color: $quiz-muted;
    }

    ul {
        margin: 0;
        padding: 0 0 0 18px;

        li {
            margin-bottom: 6px;
            font-size: 13px;
            line-height: 1.6;
            color: $quiz-text;
        }
    }
}

.quiz-night p {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: $quiz-muted;
}

.quiz-redo {
    margin-top: 16px;
    text-align: center;
}

.quiz-modal-fade-enter-active,
.quiz-modal-fade-leave-active {
    transition: opacity 0.4s $quiz-ease;
}

.quiz-modal-fade-enter-active .quiz-modal,
.quiz-modal-fade-leave-active .quiz-modal {
    transition: transform 0.46s $quiz-ease, opacity 0.4s $quiz-ease;
}

.quiz-modal-fade-enter-active .quiz-modal__header,
.quiz-modal-fade-enter-active .quiz-modal__body > * {
    animation: quiz-fade-up 0.72s $quiz-ease both;
}

.quiz-modal-fade-enter-active .quiz-modal__body > *:nth-child(2) {
    animation-delay: 0.05s;
}

.quiz-modal-fade-enter-active .quiz-modal__body > *:nth-child(3) {
    animation-delay: 0.1s;
}

.quiz-modal-fade-enter {
    opacity: 0;
}

.quiz-modal-fade-enter .quiz-modal,
.quiz-modal-fade-leave-to .quiz-modal {
    opacity: 0;
    transform: translateY(22px) scale(0.985);
}

.quiz-modal-fade-leave-to {
    opacity: 0;
}

@keyframes quiz-fade-up {
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
@media (max-width: 959px) {
    .quiz-modal--split.quiz-modal--days .quiz-modal__frame {
        height: 100%;
    }

    .quiz-modal--split .quiz-modal__body {
        flex: 1 1 auto;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .quiz-days__board {
        align-items: stretch;
    }

    .quiz-day-group {
        width: 100%;
    }

    .quiz-day-group__copy {
        min-width: 0;
    }

    .quiz-days .quiz-day-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        align-self: stretch;
        width: 100%;
        max-width: 372px;
    }

    .quiz-days .quiz-day-card {
        flex: none;
        width: auto;
        min-width: 0;
        max-width: none;
    }
}
.quiz-modal button:focus,
.quiz-modal a:focus {
    outline: none;
}

.quiz-modal button:focus-visible,
.quiz-modal a:focus-visible {
    outline: 2px solid $quiz-purple;
    outline-offset: 2px;
}
@media (max-width: 699px) {
    .quiz-modal--split .quiz-modal__frame {
        height: 100%;
        max-height: 100%;
    }

    .quiz-modal--split .quiz-modal__body {
        display: flex;
        flex-direction: column;
    }

    .quiz-screen--intro {
        display: flex;
        flex: 1 0 auto;
    }

    .quiz-intro {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
    }

    .quiz-intro__media {
        flex: 1 1 auto;
        min-height: clamp(150px, 28vh, 260px);
    }

    .quiz-intro__hero {
        object-position: center 22%;
    }

    .quiz-intro__content {
        flex: 0 0 auto;
    }
}

</style>
